import {
  HeaderBar,
  ReportComplianceSummaryCategoryBridge,
  ReportShellEthicDeptFilterSync,
  ReportShellEthicNavResetSync,
  ReportShellNurseNotesDeptFilterSync,
  ReportShellNurseNotesPeriodSync,
  ReportShellNurseNotesNavResetSync,
  ReportShellTeamDeptFilterSync,
  sortedQualityWorkLinks,
  type AppShellOutletContext,
  type NavigationTab,
} from './ComplianceHubChrome';

import React, { Suspense, lazy } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { BarChart3, Circle, FileText, Shield, Users } from "lucide-react";
import { signOut } from "firebase/auth";
import { Link, useNavigate, useLocation, useSearchParams, useOutletContext } from "react-router-dom";
import { useAuth } from "../contexts/useAuth";
import { logAuditEvent } from "../lib/audit";
import {
  clearLegacyComplianceHubReportingMonthStorage,
  persistComplianceHubReportingMonthChoice,
  resolveInitialComplianceHubReportingMonthDate,
} from "../lib/complianceHubReportingMonthStorage";
import {
  canonicalReportingMonth,
  getReportingMonth,
  isEditableMonth,
  parseReportingMonth,
  type ReportingMonth,
} from "../lib/reportingMonth";
import { useDepartments } from "../contexts/useDepartments";
import { collection, doc, getDocs, query, updateDoc, where, type DocumentReference } from "firebase/firestore";
import { MultiSelectDropdown } from "./ui/MultiSelectDropdown";
import {
  normalizeDepartmentCategories,
  normalizeDepartmentCategoryIds,
  resolveScopedDepartments,
  hasDepartmentCategoriesConfigured,
  type Department,
} from "../lib/departments";
import { resolveDepartmentDisplayName, sortDepartmentsByLofThenName } from "../lib/departmentUi";
import {
  getCrossAuditDepartmentOptions,
  HEAD_WARD_NURSE_CROSS_AUDIT_AUDITOR_DEPARTMENT_IDS,
  HEAD_WARD_NURSE_CROSS_AUDIT_HIDE_OPD_PRIMARY_PC_FOR_HOME_DEPARTMENT_IDS,
  HEAD_WARD_NURSE_ECC_ONLY_CROSS_AUDIT_HOME_DEPARTMENT_IDS,
} from "../lib/crossAudit";
import { DEFAULT_NURSING_LAYOUT_TITLES } from "../lib/crossAuditSlot";
import {
  pickHydrationDepartment,
  useSubmissionHydration,
  type CrossAuditMergeDiagnostics,
} from "../hooks/useSubmissionHydration";
import { safeLazyImport } from "../utils/safeLazyImport";
const ComplianceHubSidePanel = lazy(safeLazyImport(() => import("./ComplianceHubSidePanel")));
const Dashboard = lazy(safeLazyImport(() => import("./Dashboard")));
const ReportingTable = lazy(safeLazyImport(() => import("./ReportingTable")));
const ExportHub = lazy(safeLazyImport(() => import("./ExportHub")));
const SubmissionHistoryView = lazy(safeLazyImport(() => import("./SubmissionHistoryView")));
import {
  makeSubmissionDraftKey,
  readDraftFromLocalStorage,
  submissionDraftMatchesScope,
  writeDraftToLocalStorage,
  type SubmissionDocument,
  type SubmissionMetadata,
} from "../services/submissions";
import { type ReportingScopeOverrides, type ReportingSubmission } from "../services/dashboardData";
import { computeChartCountFromData, normalizeSubmissionShape } from "../services/submissionShape";
import { NURSING_NOTE_IDENTIFIERS, useComplianceCategories } from "../hooks/useComplianceCategories";
import {
  DEFAULT_COMPLIANCE_CATEGORIES,
  getCategoryById,
  type ComplianceCategory,
} from "../lib/complianceCategories";
import { getChecklist, type ChecklistSection } from "../lib/checklists";
import {
  completenessFromUnknownRow,
  overallConsistencyUnweightedAverage,
  perChartConsistencyPctFromStrings,
  resolveNursingFocusTreatmentsForOR,
} from "../lib/nursingComplianceScoring";
import { db } from "../lib/firestore";
import {
  getFirestoreErrorCode,
  syncParentNursingSubmittedFields,
  type ParentNursingSyncPayload,
  type ParentNursingSyncTelemetryEvent,
} from "../lib/parentNursingSync";
import { isTGOwner } from "../lib/occConstants";
import { isMedErrorSupervisionDepartmentExcluded, NURSE_NOTES_UI_EXCLUDED_DEPARTMENT_IDS } from "../lib/medErrorWardMap";
import { isNeedleAndSharpDepartmentExcluded } from "../lib/needleAndSharpPolicy";
import { isSirenBundleDepartmentExcluded, sirenColumnCompletionFlags } from "../lib/sirenBundlePolicy";
import { SirenBundleForm } from "./compliance/SirenBundleForm";
import { lofLabelForUi } from "../lib/lofs";
import { NURSE_NOTES_TEAM_LABEL } from "../lib/complianceCategoryTypes";
import { getUserAssignedDepartmentIds } from "../lib/userDepartmentScope";
import {
  APP_HOME_SAME_ROUTE_EVENT,
  scrollShellContentToTop,
} from "../lib/appShellEvents";
import {
  buildExportNavLinks,
  buildReportNavLinks,
  buildTeamNavLinks,
  canSeeEthicShellNav,
} from "../lib/shellNavDestinations";
import { getFirestorePathDepartmentId, normalizeDepartmentId } from "../utils/normalizeIds";
import { normalizeRoleId } from "../lib/idNormalization";
import { resolveCanonicalCategoryId } from "../lib/canonicalIds";
import { canViewOrgClimateSurveys } from "../lib/orgClimateSurveys/access";
import {
  isWardSubmitRetired,
  resolveWardSubmitDisplayTitle,
  wardSubmitOpenClosedMessage,
} from "../lib/wardSubmitRetired";
import { CARD_CONFIGS, DEFAULT_CHARTS, QUALITY_WORK_LINKS, REQUIRED_MED_ERROR_CHARTS, REQUIRED_SIREN_BUNDLE_CHARTS, WARD_GOOGLE_FORM_LINKS } from "../lib/compliance_constants";
import {
  getDefaultNursingChartCountForDepartment,
  getRequiredNursingChartSlots,
} from "../lib/nursingDepartmentPolicy";
import {
  canViewDepartmentScopedIncidentReportNav,
  canViewQualityWorkNav,
  isHeadWardNurseNavUser,
  isPrivilegedIncidentNavRole,
  isSupervisorNurseNavUser,
} from "../lib/privilegedIncidentNavRole";
import { NIDATA_LOADING_LABEL } from "../lib/nidataUiCopy";
import {
  SupervisorReportingFiltersProvider,
  useSupervisorReportingFilters,
} from "../contexts/SupervisorReportingFiltersContext";
import { useNavShellFilters } from "../contexts/NavShellFiltersContext";
const SupervisorReportingFilterBar = lazy(
  safeLazyImport(() =>
    import("./SupervisorReportingFilterBar").then((module) => ({
      default: module.SupervisorReportingFilterBar as React.ComponentType<unknown>,
    })),
  ),
);
const NurseNotesSubmissionScoreboard = lazy(
  safeLazyImport(() => import("./NurseNotesSubmissionScoreboard")),
);
import {
  CARD_WARNING_TITLES,
  computeAllowedCategories,
  computeNursingCardProgress,
  computeProgressPercent,
  createSupervisionRecord,
  DEVELOPMENT_ONLY_TITLES,
  formatPeriodicityLabel,
  getCategoryConfig,
  getChartCount,
  getCurrentMonthKey,
  getDefaultNursingChartTitles,
  getNursingChartTitleFallbackPool,
  getPalette,
  getPeriodLabel,
  getStoredChartCount,
  GRAY_ICON_TITLES,
  hasNursingDraftRowCompleteForProgress,
  hasNonDefaultChoice,
  INACTIVE_ICON_COLOR,
  isDocumentReference,
  KPIS_NURSING_TAB,
  LAUNCH_CATEGORY_TITLE,
  makeArray,
  MED_ERROR_TITLE,
  NEEDLE_AND_SHARP_TITLE,
  normalizeArray,
  normalizeNursingChartTitles,
  normalizeSupervisionRecords,
  readDraftWithFallback,
  resolveCategoryDocId,
  resolveSubmissionDepartmentRef,
  SIREN_BUNDLE_TITLE,
  SUPERVISOR_REPORTING_TAB,
  TEAM_RESULTS_TAB,
  TG_PROJECT_TAB,
  type ChecklistCard,
} from './ComplianceHub.helpers';
import { shouldForceHeadWardNurseHomeTab } from './ComplianceHub.logic';
import { AccessGuard, HomeNavCard, QualityLinkCard } from './ComplianceHub.widgets';

export {
  computeAllowedCategories,
  computeNursingCardProgress,
  hasNursingDraftRowCompleteForProgress,
  KPIS_NURSING_TAB,
  resolveSubmissionDepartmentRef,
  SUPERVISOR_REPORTING_TAB,
} from './ComplianceHub.helpers';

export default function ComplianceHub() {
  const { auth, authUser, canAccess, firestoreUser, isAdmin, roleContext, roleScope } = useAuth();
  const navigate = useNavigate();
  const [reportingMonthDate, setReportingMonthDate] = React.useState<Date>(() =>
    resolveInitialComplianceHubReportingMonthDate(),
  );

  React.useEffect(() => {
    clearLegacyComplianceHubReportingMonthStorage();
  }, []);
  const { departments } = useDepartments();
  const [activeCard, setActiveCard] = React.useState<ChecklistCard | null>(null);
  const headWardDefaultCardInitialized = React.useRef(false);
  const [developmentModalOpen, setDevelopmentModalOpen] = React.useState(false);
  const [developmentModalCategory, setDevelopmentModalCategory] = React.useState<string | null>(null);
  const [wardSubmitClosedNotice, setWardSubmitClosedNotice] = React.useState<{
    primary: string;
    nextStep: string;
  } | null>(null);
  const [dashboardPreselectCategory, setDashboardPreselectCategory] = React.useState<string | null>(null);

  const normalizedRoles = React.useMemo(() => {
    const sources = [
      roleContext?.generalRole,
      roleContext?.positionRole,
      ...(roleContext?.roles ?? []),
      ...(roleContext?.departmentRoles ?? []),
    ];
    return sources
      .map((role) => {
        if (!role) return null;
        if (typeof role === "string") return role.toLowerCase();
        if (typeof role === "object" && "role" in role) return String(role.role ?? "").toLowerCase();
        return String(role).toLowerCase();
      })
      .filter(Boolean) as string[];
  }, [roleContext?.departmentRoles, roleContext?.generalRole, roleContext?.positionRole, roleContext?.roles]);

  /** ADMIN / MANAGER / AUDITOR from role document id (may be set when generalRole strings omit these). */
  const hasGlobalStaffRoleId = React.useMemo(() => {
    const rid = normalizeRoleId(roleContext?.roleId);
    return rid === "ADMIN" || rid === "MANAGER" || rid === "AUDITOR";
  }, [roleContext?.roleId]);

  const isGlobalViewRole = React.useMemo(
    () =>
      normalizedRoles.includes("admin") ||
      normalizedRoles.includes("manager") ||
      normalizedRoles.includes("auditor") ||
      hasGlobalStaffRoleId,
    [normalizedRoles, hasGlobalStaffRoleId],
  );
  const isSupervisorNurseOrAudit = React.useMemo(
    () =>
      isSupervisorNurseNavUser(roleContext ?? null) ||
      normalizedRoles.some((role) => role.includes("supervisor") || role.includes("audit")) ||
      normalizedRoles.includes("admin") ||
      normalizedRoles.includes("manager") ||
      normalizedRoles.includes("auditor") ||
      hasGlobalStaffRoleId,
    [normalizedRoles, roleContext, hasGlobalStaffRoleId],
  );
  const isSupervisorNurseRole = React.useMemo(
    () =>
      normalizedRoles.includes("supervisor_nurse") ||
      (normalizedRoles.includes("supervisor") && normalizedRoles.includes("sn")) ||
      normalizedRoles.some((r) => r.includes("supervisor") && (r.includes("nurse") || r.includes("sn"))),
    [normalizedRoles],
  );
  const isNNTeamRole = React.useMemo(() => {
    const teamRoles = [roleContext?.teamRole, ...(roleContext?.teamRoles ?? [])]
      .filter(Boolean)
      .map((role) => String(role).toUpperCase());
    return teamRoles.includes("NN");
  }, [roleContext?.teamRole, roleContext?.teamRoles]);
  const hasEthicTeamRole = React.useMemo(
    () => (roleContext?.teamAllowedCategoryIds ?? []).includes("ethicSurveys"),
    [roleContext?.teamAllowedCategoryIds],
  );
  const hasTeamAccess = (roleContext?.teamAllowedCategoryIds ?? []).filter(Boolean).length > 0;
  /** Team "คณะทำงาน" tab: NN → /team, ETHIC → /team/ethic (not NN-only). */
  const showTeamWorksTab = hasTeamAccess && (isNNTeamRole || hasEthicTeamRole);
  const hasOCCTeamRole = !!roleContext?.hasOCCTeamRole;
  const isTGOwnerUser = isTGOwner(auth?.currentUser?.uid ?? authUser?.uid ?? null);
  const hasTGProjectAccess = isTGOwnerUser || hasOCCTeamRole || isGlobalViewRole;
  const defaultTabForUser = "Home";
  const [activeTab, setActiveTab] = React.useState<string>(defaultTabForUser);
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const appShellOutlet = useOutletContext<AppShellOutletContext>();
  const { teamDeptNavFilterIds } = useNavShellFilters();
  const appShellShowsMainNav = appShellOutlet?.appShellShowsMainNav === true;
  const shellTeamDeptFilterActive = appShellOutlet?.shellTeamDeptFilterActive === true;
  const shellReportNurseNotesDeptFilterActive =
    appShellOutlet?.shellReportNurseNotesDeptFilterActive === true;
  const shellReportEthicDeptFilterActive = appShellOutlet?.shellReportEthicDeptFilterActive === true;
  const shellTeamDepartmentClientFilterIds = shellTeamDeptFilterActive ? teamDeptNavFilterIds : undefined;
  /** When the global nav is visible, avoid duplicating the top "คณะทำงาน" link label in the tab strip. */
  const teamResultsTabLabel = React.useMemo(
    () => (appShellShowsMainNav ? "สำหรับคณะทำงาน" : TEAM_RESULTS_TAB),
    [appShellShowsMainNav],
  );
  const [reportingTableRefreshKey, setReportingTableRefreshKey] = React.useState(0);
  const { categories: loadedComplianceCategories, error: complianceCategoriesError } = useComplianceCategories();
  const complianceCategories = React.useMemo(
    () => (loadedComplianceCategories?.length ? loadedComplianceCategories : DEFAULT_COMPLIANCE_CATEGORIES),
    [loadedComplianceCategories],
  );
  // Check if a category is active
  const isCategoryActive = React.useCallback(
    (card: ChecklistCard) => {
      const category = getCategoryConfig(card.title, complianceCategories);
      return category?.active !== false;
    },
    [complianceCategories],
  );
  const currentUserId = auth?.currentUser?.uid ?? authUser?.uid ?? null;
  const userDepartmentRefs = React.useMemo(() => {
    const value = (firestoreUser as { department?: unknown } | null)?.department;
    if (!Array.isArray(value)) return [] as DocumentReference[];

    const refs = value.filter(isDocumentReference) as DocumentReference[];

    const seen = new Set<string>();
    const unique: DocumentReference[] = [];
    for (const ref of refs) {
      if (!ref?.id) continue;
      if (seen.has(ref.id)) continue;
      seen.add(ref.id);
      unique.push(ref);
    }

    return unique;
  }, [firestoreUser]);

  const userAssignedDepartmentIds = React.useMemo(
    () => getUserAssignedDepartmentIds(firestoreUser as Record<string, unknown>),
    [firestoreUser],
  );

  const userAssignableDepartmentRefs = React.useMemo(() => {
    const scopeDeptIds = roleScope?.edit?.departmentIds ?? roleScope?.departmentIds ?? [];
    const hasWildcard = scopeDeptIds.includes("*");
    const scopeSet = hasWildcard ? null : new Set(scopeDeptIds.map((id) => normalizeDepartmentId(id)));
    const assignedSet = new Set(userAssignedDepartmentIds.map((id) => normalizeDepartmentId(id)));
    userDepartmentRefs.forEach((ref) => {
      if (ref?.id) assignedSet.add(normalizeDepartmentId(ref.id));
    });

    const seen = new Set<string>();
    const result: DocumentReference[] = [];

    const addIfAssignable = (dept: Department) => {
      const nid = normalizeDepartmentId(dept.id);
      // MANAGER, AUDITOR, ADMIN with wildcard: see all departments (same view as SUPERVISOR_NURSE but every dept)
      if (!hasWildcard && !assignedSet.has(nid)) return;
      if (scopeSet && !scopeSet.has(nid)) return;
      if (seen.has(nid)) return;
      seen.add(nid);
      result.push(doc(db, "departments", dept.id) as DocumentReference);
    };

    departments.forEach(addIfAssignable);

    return result;
  }, [departments, roleScope?.departmentIds, roleScope?.edit?.departmentIds, userAssignedDepartmentIds, userDepartmentRefs]);

  const effectiveDepartmentRefsForSelection = React.useMemo(
    () =>
      userAssignableDepartmentRefs.length > 0
        ? userAssignableDepartmentRefs
        : userDepartmentRefs,
    [userAssignableDepartmentRefs, userDepartmentRefs],
  );

  const [selectedDepartmentRef, setSelectedDepartmentRef] = React.useState<DocumentReference | null>(() =>
    effectiveDepartmentRefsForSelection.length === 1 ? effectiveDepartmentRefsForSelection[0] : null,
  );

  React.useEffect(() => {
    if (effectiveDepartmentRefsForSelection.length === 1) {
      const only = effectiveDepartmentRefsForSelection[0];
      // Compare by id — doc() builds a new DocumentReference each memo recompute.
      setSelectedDepartmentRef((current) => (current?.id === only.id ? current : only));
      return;
    }

    setSelectedDepartmentRef((current) => {
      if (current && effectiveDepartmentRefsForSelection.some((ref) => ref.id === current.id)) {
        return current;
      }
      return null;
    });
  }, [effectiveDepartmentRefsForSelection]);

  // Supervisor Nurse LOF: restrict to user's accessible LOFs (lofRef, lofId, lofIds).
  // Any future LOF dropdown must filter options to these sources so ethic scope aligns with Firestore rules.
  const userLofRef = React.useMemo(() => {
    const refCandidate =
      (firestoreUser as { lofRef?: unknown } | null)?.lofRef ??
      (firestoreUser as { supervisedLof?: unknown } | null)?.supervisedLof ??
      (firestoreUser as { supervisor_nurse?: unknown } | null)?.supervisor_nurse ??
      (firestoreUser as { lof?: unknown } | null)?.lof ??
      (firestoreUser as { meta?: { lofRef?: unknown; supervisor_nurse?: unknown } } | null)?.meta?.lofRef ??
      (firestoreUser as { meta?: { supervisor_nurse?: unknown } } | null)?.meta?.supervisor_nurse;
    if (isDocumentReference(refCandidate)) return refCandidate;
    const lofId =
      (typeof (firestoreUser as { lofId?: unknown } | null)?.lofId === "string" &&
        (firestoreUser as { lofId: string }).lofId.trim()) ||
      (Array.isArray((firestoreUser as { lofIds?: unknown } | null)?.lofIds) &&
        (firestoreUser as { lofIds: string[] }).lofIds[0]?.trim());
    return lofId ? doc(db, "lofs", lofId) : null;
  }, [firestoreUser]);

  const [selectedLofRef, setSelectedLofRef] = React.useState<DocumentReference | null>(userLofRef);
  const [lofDepartmentIds, setLofDepartmentIds] = React.useState<string[]>([]);
  const [lofDeptLoading, setLofDeptLoading] = React.useState(false);

  React.useEffect(() => {
    setSelectedLofRef(userLofRef);
  }, [userLofRef]);

  React.useEffect(() => {
    let isActive = true;

    const loadLofDepartments = async () => {
      if (!isSupervisorNurseRole || !selectedLofRef) {
        // Object.is([], []) is false — only clear when non-empty to avoid update loops.
        setLofDepartmentIds((current) => (current.length === 0 ? current : []));
        setLofDeptLoading(false);
        return;
      }

      setLofDeptLoading(true);

      try {
        const departmentsCollection = collection(db, "departments");
        const snapshots = await Promise.all([
          getDocs(query(departmentsCollection, where("lofId", "==", selectedLofRef.id))),
          getDocs(query(departmentsCollection, where("lof", "==", selectedLofRef.id))),
          getDocs(query(departmentsCollection, where("lofRef", "==", selectedLofRef))),
          getDocs(query(departmentsCollection, where("LOF", "==", selectedLofRef))),
        ]);
        const departmentIds = new Set<string>();

        snapshots.forEach((snapshot) => {
          snapshot.docs.forEach((docSnap) => {
            const data = docSnap.data() as { departmentId?: unknown };
            const candidate =
              typeof data.departmentId === "string" && data.departmentId.trim()
                ? data.departmentId.trim()
                : docSnap.id;
            if (candidate) departmentIds.add(candidate);
          });
        });

        if (isActive) {
          setLofDepartmentIds(Array.from(departmentIds));
        }
      } catch (error) {
        console.error("Failed to load LOF departments", error);
        if (isActive) setLofDepartmentIds([]);
      } finally {
        if (isActive) setLofDeptLoading(false);
      }
    };

    void loadLofDepartments();

    return () => {
      isActive = false;
    };
  }, [isSupervisorNurseRole, selectedLofRef]);

  const userDepartmentId = React.useMemo(() => {
    const departmentId = typeof firestoreUser?.departmentId === "string" ? firestoreUser.departmentId.trim() : "";
    return departmentId || null;
  }, [firestoreUser?.departmentId]);
  const activeComplianceCategories = React.useMemo(
    () => complianceCategories.filter((category) => category.active !== false),
    [complianceCategories],
  );
  const periodLabelFor = React.useCallback(
    (title?: string) => {
      if (title === LAUNCH_CATEGORY_TITLE) {
        return CARD_CONFIGS[LAUNCH_CATEGORY_TITLE]?.label ?? "Chart";
      }
      if (title === MED_ERROR_TITLE) {
        return CARD_CONFIGS[MED_ERROR_TITLE]?.label ?? "สัปดาห์";
      }
      return getPeriodLabel(title, activeComplianceCategories);
    },
    [activeComplianceCategories],
  );

  const selectedDepartmentId = selectedDepartmentRef?.id ?? null;
  const requiresManualDepartmentSelection = effectiveDepartmentRefsForSelection.length > 1;
  const resolvedUserDepartmentId = React.useMemo(
    () => selectedDepartmentId ?? userDepartmentId,
    [selectedDepartmentId, userDepartmentId],
  );

  const reportingMonth = React.useMemo<ReportingMonth>(() => getReportingMonth(reportingMonthDate), [reportingMonthDate]);

  const showWardSubmitOpenClosedNotice = React.useCallback(
    (lookup: { categoryDocId?: string | null; title?: string | null }) => {
      const displayTitle =
        resolveWardSubmitDisplayTitle(lookup) ?? lookup.title?.trim() ?? 'ไม่ทราบชื่อหมวด';
      setWardSubmitClosedNotice(wardSubmitOpenClosedMessage(displayTitle));
      setActiveCard(null);
      setDevelopmentModalOpen(false);
      setDevelopmentModalCategory(null);
    },
    [],
  );

  const tryOpenChecklistCard = React.useCallback(
    (card: ChecklistCard | null | undefined): boolean => {
      if (!card) return false;
      if (isWardSubmitRetired({ categoryDocId: card.categoryDocId, title: card.title })) {
        showWardSubmitOpenClosedNotice({ categoryDocId: card.categoryDocId, title: card.title });
        return false;
      }
      setActiveCard(card);
      return true;
    },
    [showWardSubmitOpenClosedNotice],
  );

  const showDevelopmentModal = React.useCallback(
    (categoryTitle: string, card?: ChecklistCard) => {
      if (
        isWardSubmitRetired({
          categoryDocId: card?.categoryDocId,
          title: card?.title ?? categoryTitle,
        })
      ) {
        showWardSubmitOpenClosedNotice({
          categoryDocId: card?.categoryDocId,
          title: card?.title ?? categoryTitle,
        });
        return;
      }
      if (card) {
        setActiveCard(card);
      }
      setDevelopmentModalCategory(categoryTitle);
      setDevelopmentModalOpen(true);
    },
    [showWardSubmitOpenClosedNotice],
  );

  const closeDevelopmentModal = React.useCallback(() => {
    setDevelopmentModalOpen(false);
    setActiveCard(null);
  }, []);

  const isHeadWardNurse = React.useMemo(
    () =>
      normalizedRoles.includes("head_ward_nurse") ||
      roleContext?.departmentRole === "HEAD_WARD_NURSE" ||
      roleContext?.positionRole === "HEAD_WARD_NURSE" ||
      (roleContext?.roles ?? []).some((r) => r.toUpperCase() === "HEAD_WARD_NURSE") ||
      (roleContext?.departmentRoles ?? []).some(
        (d) => (typeof d === "object" && "role" in d ? String(d.role) : String(d)) === "HEAD_WARD_NURSE"
      ),
    [normalizedRoles, roleContext?.departmentRole, roleContext?.departmentRoles, roleContext?.positionRole, roleContext?.roles],
  );

  /** App shell already has global nav + shell export; hide duplicate sub-header (รายงานรวม / ส่งออก tabs). */
  const hideComplianceHubTabStrip = appShellShowsMainNav && (isSupervisorNurseOrAudit || isHeadWardNurse);

  const canViewCompliance = React.useMemo(() => canAccess('compliance', 'view'), [canAccess]);
  const canEditCompliance = React.useMemo(
    () => canAccess('compliance', 'edit', { audit: true, reason: 'Compliance workspace edit gate' }),
    [canAccess],
  );
  const canViewDashboard = React.useMemo(() => canAccess('dashboard', 'view'), [canAccess]);
  const canViewSummary = React.useMemo(() => canAccess('summary_table', 'view'), [canAccess]);
  const showQualityWorkHomeCard = React.useMemo(
    () => canViewQualityWorkNav(roleContext ?? null, Boolean(isAdmin)),
    [isAdmin, roleContext],
  );

  const teamAllowedCategoryIdsForHub = roleContext?.teamAllowedCategoryIds ?? [];
  const hasHubIncidentCategoryAccess = React.useCallback(
    (categoryId: string) => {
      if (teamAllowedCategoryIdsForHub.includes(categoryId)) return true;
      const generalRole = roleContext?.generalRole?.toUpperCase();
      const positionRole = roleContext?.positionRole?.toUpperCase();
      if (generalRole === 'ADMIN' || generalRole === 'MANAGER' || generalRole === 'AUDITOR') return true;
      if (positionRole === 'SUPERVISOR_NURSE' || positionRole === 'SN') return true;
      if (positionRole === 'HEAD_WARD_NURSE' || positionRole === 'HWN') return true;
      return userAssignedDepartmentIds.length > 0;
    },
    [roleContext?.generalRole, roleContext?.positionRole, teamAllowedCategoryIdsForHub, userAssignedDepartmentIds.length],
  );
  const hasMedErrorIncidentAccess = React.useMemo(
    () => hasHubIncidentCategoryAccess('med_error_incident'),
    [hasHubIncidentCategoryAccess],
  );
  const hasFallIncidentAccess = React.useMemo(
    () => hasHubIncidentCategoryAccess('fall_incident'),
    [hasHubIncidentCategoryAccess],
  );
  const hasKpoaIncidentAccess = React.useMemo(
    () => hasHubIncidentCategoryAccess('kpoa'),
    [hasHubIncidentCategoryAccess],
  );
  const canSeeDepartmentScopedIncidentReports = React.useMemo(
    () => canViewDepartmentScopedIncidentReportNav(roleContext ?? null),
    [roleContext],
  );
  const canSeeEthicReportNav = React.useMemo(
    () => canSeeEthicShellNav(roleContext ?? null, canSeeDepartmentScopedIncidentReports),
    [canSeeDepartmentScopedIncidentReports, roleContext],
  );
  const privilegedIncidentNav = React.useMemo(
    () => isPrivilegedIncidentNavRole(roleContext ?? null),
    [roleContext],
  );
  const canOpenExportHub = React.useMemo(
    () =>
      isHeadWardNurseNavUser(roleContext ?? null) ||
      (privilegedIncidentNav && (hasMedErrorIncidentAccess || hasFallIncidentAccess)),
    [hasFallIncidentAccess, hasMedErrorIncidentAccess, privilegedIncidentNav, roleContext],
  );
  const reportHubLinks = React.useMemo(
    () =>
      buildReportNavLinks({
        roleContext: roleContext ?? null,
        isAdmin: Boolean(isAdmin),
        uid: authUser?.uid ?? null,
        canSeeDepartmentScopedIncidentReports,
        canSeeEthicReportNav,
        hasMedErrorIncidentAccess,
        hasFallIncidentAccess,
        hasKpoaIncidentAccess,
      }),
    [
      authUser?.uid,
      canSeeDepartmentScopedIncidentReports,
      canSeeEthicReportNav,
      hasFallIncidentAccess,
      hasKpoaIncidentAccess,
      hasMedErrorIncidentAccess,
      isAdmin,
      roleContext,
    ],
  );
  const teamHubLinks = React.useMemo(
    () =>
      buildTeamNavLinks({
        roleContext: roleContext ?? null,
        isAdmin: Boolean(isAdmin),
        canViewOrgClimate: canViewOrgClimateSurveys(authUser?.uid),
        hasTGProjectAccess,
        includeKpoa: hasKpoaIncidentAccess,
      }),
    [authUser?.uid, hasKpoaIncidentAccess, hasTGProjectAccess, isAdmin, roleContext],
  );
  const exportHubLinks = React.useMemo(
    () => buildExportNavLinks({ canOpenExportHub }),
    [canOpenExportHub],
  );

  /** Shell nav links: `/report?category=…` — drives รายงานรวม filters + Dashboard preselect */
  const reportingCategoryFromQuery = React.useMemo(() => {
    const raw = searchParams.get('category');
    if (!raw?.trim()) return null;
    const c = resolveCanonicalCategoryId(raw.trim());
    if (c === 'nurse_notes' || c === 'ethicSurveys' || c === 'siren_bundle') return c;
    if (c === 'orgClimateSurveys' && canViewOrgClimateSurveys(authUser?.uid)) return c;
    return null;
  }, [searchParams, authUser?.uid]);
  const shellNurseNotesPeriodControls =
    shellReportNurseNotesDeptFilterActive || reportingCategoryFromQuery === 'nurse_notes';
  const openExportTabFromQuery = React.useMemo(() => {
    const raw = searchParams.get('tab')?.trim().toLowerCase();
    return raw === 'export' || raw === 'ส่งออก';
  }, [searchParams]);

  const setActiveReportingMonth = React.useCallback((month: ReportingMonth) => {
    const parsed = parseReportingMonth(month);
    setReportingMonthDate(parsed);
    persistComplianceHubReportingMonthChoice(month);
  }, []);

  const tabs = React.useMemo<NavigationTab[]>(() => {
    if (isSupervisorNurseOrAudit) {
      const base: NavigationTab[] = [{ label: "Home" }, { label: SUPERVISOR_REPORTING_TAB }];
      if (showTeamWorksTab) base.push({ label: teamResultsTabLabel });
      if (hasTGProjectAccess) base.push({ label: TG_PROJECT_TAB });
      if (!appShellShowsMainNav) base.push({ label: KPIS_NURSING_TAB });
      base.push({ label: "ส่งออก", disabled: !canViewSummary });
      return base;
    }

    const base = [
      { label: "Home" },
      { label: "ประวัติ", disabled: !canViewCompliance },
      { label: "Dashboard", disabled: !canViewDashboard },
      { label: "ตารางข้อมูล", disabled: !canViewSummary },
      { label: "ส่งออก", disabled: !canViewSummary },
    ];
    if (showTeamWorksTab) base.push({ label: teamResultsTabLabel });
    if (hasTGProjectAccess) base.push({ label: TG_PROJECT_TAB });
    if (!appShellShowsMainNav) base.push({ label: KPIS_NURSING_TAB });
    return base;
  }, [
    appShellShowsMainNav,
    canViewCompliance,
    canViewDashboard,
    canViewSummary,
    showTeamWorksTab,
    hasTGProjectAccess,
    isSupervisorNurseOrAudit,
    teamResultsTabLabel,
  ]);

  React.useEffect(() => {
    if (!isSupervisorNurseOrAudit) return;
    if (tabs.some((t) => t.label === activeTab && !t.disabled)) return;
    setActiveTab(SUPERVISOR_REPORTING_TAB);
  }, [activeTab, isSupervisorNurseOrAudit, tabs]);

  React.useEffect(() => {
    const path = location.pathname;
    if (path.startsWith('/team/occ')) {
      if (tabs.some((t) => t.label === TG_PROJECT_TAB)) setActiveTab(TG_PROJECT_TAB);
    } else if (
      (path === '/team' || path === '/team/ethic') &&
      tabs.some((t) => t.label === teamResultsTabLabel)
    ) {
      setActiveTab(teamResultsTabLabel);
    } else if (path === '/report' && openExportTabFromQuery && tabs.some((t) => t.label === 'ส่งออก' && !t.disabled)) {
      setActiveTab('ส่งออก');
    } else if (path === '/report' && reportingCategoryFromQuery) {
      if (isSupervisorNurseOrAudit && tabs.some((t) => t.label === SUPERVISOR_REPORTING_TAB)) {
        setActiveTab(SUPERVISOR_REPORTING_TAB);
      } else if (canViewDashboard && tabs.some((t) => t.label === 'Dashboard')) {
        setActiveTab('Dashboard');
      }
      setDashboardPreselectCategory(reportingCategoryFromQuery);
    } else if ((path === '/kpis' || path === '/kpis-nursing') && tabs.some((t) => t.label === KPIS_NURSING_TAB)) {
      setActiveTab(KPIS_NURSING_TAB);
    }
  }, [
    location.pathname,
    openExportTabFromQuery,
    reportingCategoryFromQuery,
    tabs,
    teamResultsTabLabel,
    isSupervisorNurseOrAudit,
    canViewDashboard,
  ]);

  const landingTabLabel = "Home";

  /** Mega menu "หน้าแรก" while already on /report: return HWN users to the compliance form tab. */
  React.useEffect(() => {
    const onAppHomeSameRoute = () => {
      if (searchParams.get("category") || searchParams.get("tab")) {
        navigate("/report", { replace: true });
      }
      setActiveTab(landingTabLabel);
      setDashboardPreselectCategory(null);
      scrollShellContentToTop();
    };
    window.addEventListener(APP_HOME_SAME_ROUTE_EVENT, onAppHomeSameRoute);
    return () => window.removeEventListener(APP_HOME_SAME_ROUTE_EVENT, onAppHomeSameRoute);
  }, [landingTabLabel, navigate, searchParams]);

  const headWardDepartments = React.useMemo(() => {
    const uid = currentUserId ?? '';
    const email = authUser?.email ?? (firestoreUser as any)?.email ?? '';
    const username = email.includes('@') ? email.split('@')[0] : email;

    const fromDeptAssignment = departments.filter((dept) => {
      const assigned = (dept.headWardNurseId ?? '').toString().trim();
      if (!assigned) return false;

      return (
        assigned === uid ||
        (email && assigned.toLowerCase() === email.toLowerCase()) ||
        (username && assigned === username)
      );
    });

    if (fromDeptAssignment.length > 0) return fromDeptAssignment;

    // Fallback: user may be HWN via departmentRoleById (canonical) even when
    // department.headWardNurseId is not set. Use user-assigned department ids.
    if (isHeadWardNurse && userAssignedDepartmentIds.length > 0) {
      const assignedSet = new Set(userAssignedDepartmentIds.map((id) => normalizeDepartmentId(id)));
      return departments.filter((dept) => assignedSet.has(normalizeDepartmentId(dept.id)));
    }

    return fromDeptAssignment;
  }, [authUser?.email, currentUserId, departments, firestoreUser, isHeadWardNurse, userAssignedDepartmentIds]);

  const headWardDepartmentIds = React.useMemo(
    () => headWardDepartments.map((department) => department.id),
    [headWardDepartments],
  );

  const effectiveRoleScope = React.useMemo(() => {
    if (!roleScope) return roleScope;
    // ADMIN / MANAGER / AUDITOR must keep full wildcard scope; do not narrow for dual HWN assignment.
    if (isGlobalViewRole) return roleScope;
    if (!isHeadWardNurse || !headWardDepartmentIds.length) return roleScope;

    return {
      ...roleScope,
      departmentIds: headWardDepartmentIds,
      view: {
        ...roleScope.view,
        departmentIds: headWardDepartmentIds,
      },
      edit: {
        ...roleScope.edit,
        departmentIds: headWardDepartmentIds,
      },
    };
  }, [headWardDepartmentIds, isGlobalViewRole, isHeadWardNurse, roleScope]);

  const hasWildcardDepartmentScope = React.useMemo(() => {
    const scopes = [roleScope?.departmentIds, roleScope?.view?.departmentIds, roleScope?.edit?.departmentIds];
    return scopes.some((scope) => scope?.includes("*"));
  }, [roleScope?.departmentIds, roleScope?.edit?.departmentIds, roleScope?.view?.departmentIds]);

  const scopedDepartments = React.useMemo(
    () => {
      const scoped = resolveScopedDepartments(
        departments,
        roleScope?.edit?.departmentIds ?? roleScope?.departmentIds ?? [],
        roleContext?.lineOfWorkDepartmentIds ?? [],
      );
      // Global view (manager/auditor) or wildcard scope: show all departments
      if (hasWildcardDepartmentScope && scoped.length > 0) return scoped;

      const userDepartment = resolvedUserDepartmentId
        ? departments.find((department) => department.id === resolvedUserDepartmentId)
        : null;
      if (userDepartment) return [userDepartment];

      if (isHeadWardNurse) {
        if (headWardDepartments.length) return headWardDepartments;
        return [];
      }

      if (scoped.length) return scoped;
      if (headWardDepartments.length) return headWardDepartments;

      return scoped;
    },
    [
      departments,
      headWardDepartments,
      hasWildcardDepartmentScope,
      isHeadWardNurse,
      roleContext?.lineOfWorkDepartmentIds,
      roleScope?.departmentIds,
      roleScope?.edit?.departmentIds,
      resolvedUserDepartmentId,
    ],
  );

  const scopedTeamIds = React.useMemo(
    () =>
      Array.from(
        new Set([...(roleScope?.teamIds ?? []), ...(roleScope?.view?.teamIds ?? []), ...(roleScope?.edit?.teamIds ?? [])]),
      ),
    [roleScope?.edit?.teamIds, roleScope?.teamIds, roleScope?.view?.teamIds],
  );

  const allowedCategories = React.useMemo(
    () => {
      const base = computeAllowedCategories(
        scopedDepartments,
        hasWildcardDepartmentScope,
        isAdmin,
        activeComplianceCategories,
        auth?.currentUser?.uid ?? authUser?.uid,
        scopedTeamIds,
        isNNTeamRole,
        roleContext?.teamAllowedCategoryIds ?? [],
      );

      // Normal compliance hub: supervisors should always see Ethic + Med Error Incident + Fall Incident
      // within their LOF department scope (team pages remain gated by team roles).
      if (!isAdmin && isSupervisorNurseRole) {
        const byId = new Map(activeComplianceCategories.map((c) => [c.id, c.title].filter(Boolean) as [string, string]));
        const forceIds = ['ethicSurveys', 'med_error_incident', 'fall_incident'] as const;
        const out = new Set(base);
        for (const id of forceIds) {
          const title = byId.get(id);
          if (title?.trim()) out.add(title);
        }
        return Array.from(out);
      }

      return base;
    },
    [
      activeComplianceCategories,
      auth?.currentUser?.uid,
      authUser?.uid,
      hasWildcardDepartmentScope,
      isAdmin,
      isSupervisorNurseRole,
      roleContext?.teamAllowedCategoryIds,
      scopedDepartments,
      scopedTeamIds,
      isNNTeamRole,
    ],
  );

  const departmentSelectionOptions = React.useMemo(
    () =>
      sortDepartmentsByLofThenName(
        effectiveDepartmentRefsForSelection.map((ref) => {
          const department = departments.find((dept) => dept.id === ref.id);
          const displayName = resolveDepartmentDisplayName(ref.id, department?.name);
          return {
            ref,
            label: displayName,
            id: ref.id,
            name: displayName,
            lofId: department?.lofId ?? '',
          };
        }),
      ).map(({ ref, label }) => ({ ref, label })),
    [departments, effectiveDepartmentRefsForSelection],
  );

  const homeDepartmentSelectionOptions = React.useMemo(
    () =>
      departmentSelectionOptions.filter(
        (o) => !NURSE_NOTES_UI_EXCLUDED_DEPARTMENT_IDS.has(o.ref.id),
      ),
    [departmentSelectionOptions],
  );

  const mainTabDepartmentPickerOptions =
    activeTab === "Home" ? homeDepartmentSelectionOptions : departmentSelectionOptions;

  React.useEffect(() => {
    if (activeTab !== "Home") return;
    if (!selectedDepartmentId) return;
    if (!NURSE_NOTES_UI_EXCLUDED_DEPARTMENT_IDS.has(selectedDepartmentId)) return;
    setSelectedDepartmentRef(null);
  }, [activeTab, selectedDepartmentId]);

  const defaultDepartmentId = React.useMemo(() => {
    const userDepartment = resolvedUserDepartmentId
      ? departments.find((department) => department.id === resolvedUserDepartmentId)
      : null;
    if (userDepartment) return userDepartment.id;

    if (requiresManualDepartmentSelection && !selectedDepartmentId) return null;

    const scopedId = pickHydrationDepartment(roleScope);
    if (scopedId) return scopedId;

    if (isHeadWardNurse && headWardDepartments.length) {
      return headWardDepartments[0].id;
    }

    return scopedDepartments[0]?.id ?? null;
  }, [
    departments,
    headWardDepartments,
    isHeadWardNurse,
    requiresManualDepartmentSelection,
    resolvedUserDepartmentId,
    roleScope,
    scopedDepartments,
    selectedDepartmentId,
  ]);

  const [activeDepartmentId, setActiveDepartmentId] = React.useState<string | null>(defaultDepartmentId);

  const activeDepartment = React.useMemo(
    () => departments.find((dept) => dept.id === activeDepartmentId) ?? null,
    [activeDepartmentId, departments],
  );

  const submissionDepartmentId = React.useMemo(
    () => {
      // Never return selectedLofRef.id — it is a LOF ID, not a department ID.
      if (isSupervisorNurseRole && selectedLofRef) {
        return (
          selectedDepartmentId ??
          activeDepartmentId ??
          (lofDepartmentIds.length ? lofDepartmentIds[0] : null)
        );
      }
      return selectedDepartmentId ?? activeDepartmentId ?? null;
    },
    [activeDepartmentId, isSupervisorNurseRole, selectedDepartmentId, selectedLofRef, lofDepartmentIds],
  );

  const chartCountFor = React.useCallback(
    (title?: string) => {
      // Firestore complianceCategories often copy needle defaults (charts: 10, ครั้งที่). Hub must follow policy.
      if (!title) return DEFAULT_CHARTS;
      if (title === LAUNCH_CATEGORY_TITLE || NURSING_NOTE_IDENTIFIERS.has(title)) {
        return getDefaultNursingChartCountForDepartment(submissionDepartmentId);
      }
      if (title === MED_ERROR_TITLE) {
        return REQUIRED_MED_ERROR_CHARTS;
      }
      if (title === SIREN_BUNDLE_TITLE) {
        return REQUIRED_SIREN_BUNDLE_CHARTS;
      }
      return getChartCount(title, activeComplianceCategories);
    },
    [activeComplianceCategories, submissionDepartmentId],
  );

  const nursingRequiredChartSlots = React.useMemo(
    () => getRequiredNursingChartSlots(submissionDepartmentId),
    [submissionDepartmentId],
  );

  const reportingScopeOverrides = React.useMemo<ReportingScopeOverrides | undefined>(() => {
    // Global view: manager / auditor / admin — default to all departments; narrow when user picks a ward in the dropdown.
    if (isGlobalViewRole) {
      return {
        departmentIds: selectedDepartmentId ? [selectedDepartmentId] : ['*'],
      };
    }

    // SN before HWN: dual-role (SN+HWN) keeps LOF-wide Dashboard/Table scope.
    // ExportHub clamps HWN export reads to own wards so LOF overrides here do not break export.
    // SN: LOF-resolved departments. NN + nurse_notes hospital-wide scope is applied in Dashboard / dashboardData (not here).
    if (isSupervisorNurseRole) {
      // While LOF depts load from Firestore, omit override so Dashboard uses Auth-resolved LOF ids (no empty flash).
      if (selectedLofRef && lofDeptLoading) {
        return undefined;
      }
      if (selectedLofRef && !lofDeptLoading && lofDepartmentIds.length > 0) {
        return {
          lofIds: [selectedLofRef.id],
          departmentIds: lofDepartmentIds,
        };
      }
      if (selectedLofRef && !lofDeptLoading && lofDepartmentIds.length === 0) {
        return { departmentIds: [] };
      }
      // No LOF ref: use roleScope from Auth (LOF-derived or explicit dept list only)
      const snDeptIds = roleScope?.view?.departmentIds ?? roleScope?.departmentIds ?? [];
      return { departmentIds: snDeptIds.filter((id): id is string => typeof id === 'string' && id !== '*') };
    }

    if (isHeadWardNurse) {
      // "เลือกแผนก" when user heads multiple wards: narrow Dashboard/Table (incl. ethic) to that ward only.
      const selectableDeptIds = new Set(
        effectiveDepartmentRefsForSelection.map((r) => normalizeDepartmentId(r.id)),
      );
      if (
        selectedDepartmentId &&
        selectableDeptIds.has(normalizeDepartmentId(selectedDepartmentId))
      ) {
        return { departmentIds: [selectedDepartmentId] };
      }
      // Prefer departments where user is HWN (headWardNurseId or departmentRoleById)
      if (headWardDepartmentIds.length > 0) {
        return {
          departmentIds: headWardDepartmentIds,
        };
      }
      // Fallback: use user-assigned departments so table shows data when HWN assignment is missing
      if (userAssignedDepartmentIds.length > 0) {
        return {
          departmentIds: userAssignedDepartmentIds,
        };
      }
      return undefined;
    }

    // Fallback: when roleScope has no departmentIds (e.g. not yet loaded or user doc missing departments),
    // use the department the user is currently viewing — same source as History tab, so table shows data.
    const roleDeptIds = roleScope?.view?.departmentIds ?? roleScope?.departmentIds ?? [];
    const hasRoleScopeDepts = roleDeptIds.some((id) => id && id !== '*');
    if (!hasRoleScopeDepts) {
      const contextDeptId = submissionDepartmentId ?? activeDepartmentId;
      if (contextDeptId) {
        return { departmentIds: [contextDeptId] };
      }
    }

    return undefined;
  }, [
    activeDepartmentId,
    effectiveDepartmentRefsForSelection,
    headWardDepartmentIds,
    isHeadWardNurse,
    isGlobalViewRole,
    isSupervisorNurseRole,
    lofDepartmentIds,
    lofDeptLoading,
    roleScope?.departmentIds,
    roleScope?.view?.departmentIds,
    selectedDepartmentId,
    selectedLofRef,
    submissionDepartmentId,
    userAssignedDepartmentIds,
  ]);

  const submissionDepartmentRef = React.useMemo(
    () =>
      resolveSubmissionDepartmentRef({
        isSupervisorNurseRole,
        selectedDepartmentRef,
        submissionDepartmentId,
      }),
    [isSupervisorNurseRole, selectedDepartmentRef, submissionDepartmentId],
  );
  const rawLofId = React.useMemo(() => {
    if (selectedLofRef?.id) return selectedLofRef.id;
    const departmentLofId = activeDepartment?.lofId?.trim() ?? "";
    return departmentLofId || null;
  }, [activeDepartment?.lofId, selectedLofRef]);
  const submissionLofRef = React.useMemo(() => {
    if (selectedLofRef) return selectedLofRef;
    if (rawLofId) return doc(db, "lofs", rawLofId);
    return null;
  }, [rawLofId, selectedLofRef]);
  const submissionLofId = React.useMemo(() => submissionLofRef?.id ?? rawLofId, [rawLofId, submissionLofRef]);

  const submissionMetadata = React.useMemo<SubmissionMetadata>(
    () => ({
      submittedBy: auth?.currentUser?.uid ?? currentUserId ?? null,
      submittedByEmail: auth?.currentUser?.email ?? authUser?.email ?? null,
      submittedByName: auth?.currentUser?.displayName ?? authUser?.displayName ?? authUser?.email ?? null,
      submitterRole: roleContext?.positionRole ?? roleContext?.generalRole ?? null,
      departmentId: isSupervisorNurseRole ? null : submissionDepartmentId ?? null,
      departmentName: isSupervisorNurseRole ? null : activeDepartment?.name ?? null,
      lofId: submissionLofId,
      departmentRef: submissionDepartmentRef ?? null,
      lofRef: submissionLofRef ?? null,
    }),
    [
      activeDepartment?.lofId,
      activeDepartment?.name,
      auth?.currentUser?.displayName,
      auth?.currentUser?.email,
      auth?.currentUser?.uid,
      authUser?.displayName,
      authUser?.email,
      currentUserId,
      roleContext?.generalRole,
      roleContext?.positionRole,
      isSupervisorNurseRole,
      selectedLofRef,
      submissionDepartmentId,
      submissionDepartmentRef,
      submissionLofId,
      submissionLofRef,
    ],
  );

  React.useEffect(() => {
    setActiveDepartmentId((current) => {
      if (selectedDepartmentId) return selectedDepartmentId;
      if (
        current &&
        (current === resolvedUserDepartmentId ||
          hasWildcardDepartmentScope ||
          scopedDepartments.some((dept) => dept.id === current))
      ) {
        return current;
      }
      if (!requiresManualDepartmentSelection && resolvedUserDepartmentId) return resolvedUserDepartmentId;
      if (requiresManualDepartmentSelection) return null;
      return defaultDepartmentId;
    });
  }, [
    defaultDepartmentId,
    hasWildcardDepartmentScope,
    requiresManualDepartmentSelection,
    resolvedUserDepartmentId,
    scopedDepartments,
    selectedDepartmentId,
  ]);

  const REPORT_CATEGORY_IDS = React.useMemo(
    () => new Set(['nurse_notes', 'ethicSurveys', 'med_error', 'siren_bundle', 'needleandsharp']),
    [],
  );
  const REPORT_CATEGORY_TITLES = React.useMemo(
    () =>
      new Set([
        LAUNCH_CATEGORY_TITLE,
        MED_ERROR_TITLE,
        SIREN_BUNDLE_TITLE,
        NEEDLE_AND_SHARP_TITLE,
        "บรรยากาศองค์กรจริยธรรม",
      ]),
    [],
  );
  const hydrationCategories = React.useMemo(() => {
    const primaryDepartment = departments.find((dept) => dept.id === activeDepartmentId);
    const departmentCategories = primaryDepartment
      ? normalizeDepartmentCategories(primaryDepartment, activeComplianceCategories)
      : [];
    let raw = departmentCategories.length ? departmentCategories : allowedCategories;
    if (isHeadWardNurse && !isSupervisorNurseOrAudit) {
      raw = raw.filter(
        (idOrTitle) =>
          REPORT_CATEGORY_IDS.has(idOrTitle) ||
          REPORT_CATEGORY_TITLES.has(idOrTitle),
      );
      const teamEthic = (roleContext?.teamAllowedCategoryIds ?? []).filter(
        (id) => id === 'ethicSurveys' || id === 'บรรยากาศองค์กรจริยธรรม',
      );
      if (teamEthic.length > 0 && !raw.some((v) => v === 'ethicSurveys' || v === 'บรรยากาศองค์กรจริยธรรม')) {
        raw = [...raw, 'ethicSurveys'];
      }
    }
    raw = raw.filter(
      (v) =>
        typeof v === 'string' &&
        resolveCategoryDocId(v, activeComplianceCategories) !== 'med_error_incident',
    );
    const idToTitle = new Map(activeComplianceCategories.map((c) => [c.id ?? '', c.title ?? '']));
    return raw.map((v) => idToTitle.get(v) || v);
  }, [
    activeComplianceCategories,
    activeDepartmentId,
    allowedCategories,
    departments,
    isHeadWardNurse,
    isSupervisorNurseOrAudit,
    roleContext?.teamAllowedCategoryIds,
    REPORT_CATEGORY_IDS,
    REPORT_CATEGORY_TITLES,
  ]);

  React.useEffect(() => {
    if (
      activeCard &&
      hydrationCategories.length > 0 &&
      !hydrationCategories.includes(activeCard.title) &&
      !developmentModalOpen
    ) {
      setActiveCard(null);
      if (isHeadWardNurse) {
        headWardDefaultCardInitialized.current = false;
      }
    }
  }, [activeCard, developmentModalOpen, headWardDefaultCardInitialized, hydrationCategories, isHeadWardNurse]);

  React.useEffect(() => {
    if (!tabs.find((tab) => tab.label === activeTab && !tab.disabled)) {
      const fallbackTab = tabs.find((tab) => !tab.disabled);
      if (fallbackTab) {
        setActiveTab(fallbackTab.label);
      }
    }
  }, [activeTab, tabs]);

  const nursingCategoryDocId = React.useMemo(
    () => resolveCategoryDocId(LAUNCH_CATEGORY_TITLE, complianceCategories),
    [complianceCategories],
  );
  const needleCategoryDocId = React.useMemo(
    () => resolveCategoryDocId(NEEDLE_AND_SHARP_TITLE, complianceCategories),
    [complianceCategories],
  );
  const drugCategoryDocId = React.useMemo(
    () => resolveCategoryDocId(MED_ERROR_TITLE, complianceCategories),
    [complianceCategories],
  );
  const sirenCategoryDocId = React.useMemo(
    () => resolveCategoryDocId(SIREN_BUNDLE_TITLE, complianceCategories),
    [complianceCategories],
  );
  const supervisionCategoryDocId = React.useMemo(
    () => resolveCategoryDocId("นิเทศทางการพยาบาล", complianceCategories),
    [complianceCategories],
  );
  const monthKeyNursing = React.useMemo(
    () => getCurrentMonthKey(nursingCategoryDocId || LAUNCH_CATEGORY_TITLE, reportingMonthDate, submissionDepartmentId, currentUserId),
    [currentUserId, nursingCategoryDocId, reportingMonthDate, submissionDepartmentId],
  );
  const legacyMonthKeyNursing = React.useMemo(
    () => getCurrentMonthKey(LAUNCH_CATEGORY_TITLE, reportingMonthDate, submissionDepartmentId, currentUserId),
    [currentUserId, reportingMonthDate, submissionDepartmentId],
  );
  const monthKeyNeedle = React.useMemo(
    () => getCurrentMonthKey(needleCategoryDocId || NEEDLE_AND_SHARP_TITLE, reportingMonthDate, submissionDepartmentId, currentUserId),
    [currentUserId, needleCategoryDocId, reportingMonthDate, submissionDepartmentId],
  );
  const legacyMonthKeyNeedle = React.useMemo(
    () => getCurrentMonthKey(NEEDLE_AND_SHARP_TITLE, reportingMonthDate, submissionDepartmentId, currentUserId),
    [currentUserId, reportingMonthDate, submissionDepartmentId],
  );
  const monthKeyDrug = React.useMemo(
    () => getCurrentMonthKey(drugCategoryDocId || MED_ERROR_TITLE, reportingMonthDate, submissionDepartmentId, currentUserId),
    [currentUserId, drugCategoryDocId, reportingMonthDate, submissionDepartmentId],
  );
  const legacyMonthKeyDrug = React.useMemo(
    () => getCurrentMonthKey(MED_ERROR_TITLE, reportingMonthDate, submissionDepartmentId, currentUserId),
    [currentUserId, reportingMonthDate, submissionDepartmentId],
  );
  const monthKeySiren = React.useMemo(
    () => getCurrentMonthKey(sirenCategoryDocId || SIREN_BUNDLE_TITLE, reportingMonthDate, submissionDepartmentId, currentUserId),
    [currentUserId, reportingMonthDate, sirenCategoryDocId, submissionDepartmentId],
  );
  const legacyMonthKeySiren = React.useMemo(
    () => getCurrentMonthKey(SIREN_BUNDLE_TITLE, reportingMonthDate, submissionDepartmentId, currentUserId),
    [currentUserId, reportingMonthDate, submissionDepartmentId],
  );
  const monthKeySupervision = React.useMemo(
    () => getCurrentMonthKey(supervisionCategoryDocId || "นิเทศทางการพยาบาล", reportingMonthDate, submissionDepartmentId, currentUserId),
    [currentUserId, reportingMonthDate, submissionDepartmentId, supervisionCategoryDocId],
  );
  const legacyMonthKeySupervision = React.useMemo(
    () => getCurrentMonthKey("นิเทศทางการพยาบาล", reportingMonthDate, submissionDepartmentId, currentUserId),
    [currentUserId, reportingMonthDate, submissionDepartmentId],
  );

  /** Must stay aligned with submission and clear-all category logic (resolveCategoryDocId). */
  const computeMonthKey = React.useCallback(
    (category: string, userIdOverride?: string | null, storageDepartmentId?: string | null) => {
      const dept = storageDepartmentId ?? submissionDepartmentId;
      return getCurrentMonthKey(
        resolveCategoryDocId(category, complianceCategories) || category,
        reportingMonthDate,
        dept,
        userIdOverride ?? currentUserId,
      );
    },
    [complianceCategories, currentUserId, reportingMonthDate, submissionDepartmentId],
  );

  const mergeWithLocalSubmittedList = React.useMemo(
    () =>
      [
        LAUNCH_CATEGORY_TITLE,
        ...(nursingCategoryDocId && nursingCategoryDocId !== LAUNCH_CATEGORY_TITLE ? [nursingCategoryDocId] : []),
      ],
    [nursingCategoryDocId],
  );

  const recomputeFromStorageRef = React.useRef<() => void>(() => {});

  const [formReloadTrigger, setFormReloadTrigger] = React.useState(0);
  const hydrationDepartmentIdsToTry = React.useMemo(() => {
    if (!submissionDepartmentId) return [];
    // If user explicitly selected a ward from the dropdown, do not hydrate from other wards.
    // Cross-department fallback here can make one ward display another ward's draft/submission state.
    if (requiresManualDepartmentSelection && selectedDepartmentId) return [];
    const ids = headWardDepartmentIds.length > 0
      ? headWardDepartmentIds
      : (roleScope?.view?.departmentIds ?? roleScope?.departmentIds ?? []);
    return (Array.isArray(ids) ? ids : []).filter(
      (id): id is string => typeof id === 'string' && id !== '*' && id !== submissionDepartmentId,
    );
  }, [
    headWardDepartmentIds,
    requiresManualDepartmentSelection,
    roleScope?.departmentIds,
    roleScope?.view?.departmentIds,
    selectedDepartmentId,
    submissionDepartmentId,
  ]);

  const hydrationState = useSubmissionHydration({
    enabled: isHeadWardNurse || canEditCompliance,
    departmentId: submissionDepartmentId,
    reportingMonth,
    categories: hydrationCategories,
    makeMonthKey: computeMonthKey,
    userId: currentUserId,
    mergeWithLocalSubmitted: mergeWithLocalSubmittedList,
    departmentIdsToTry: hydrationDepartmentIdsToTry,
    onCategoryHydrated: (category) => {
      const resolved = resolveCategoryDocId(category, complianceCategories) || category;
      if (
        category === LAUNCH_CATEGORY_TITLE ||
        resolved === "nurse_notes" ||
        category === nursingCategoryDocId
      ) {
        recomputeFromStorageRef.current();
        setFormReloadTrigger((t) => t + 1);
      }
    },
  });

  const hydrationToken = React.useMemo(
    () => `${reportingMonth}|${hydrationState.hydratedCategories.join("|")}|${hydrationState.loading}|${formReloadTrigger}`,
    [reportingMonth, hydrationState.hydratedCategories, hydrationState.loading, formReloadTrigger],
  );

  const [nursingCharts, setNursingCharts] = React.useState<number>(() =>
    getStoredChartCount(
      monthKeyNursing,
      legacyMonthKeyNursing,
      chartCountFor("บันทึกทางการพยาบาล"),
      submissionDepartmentId,
    ),
  );
  const [chartTitleList, setChartTitleList] = React.useState<string[]>(() =>
    getDefaultNursingChartTitles(
      getStoredChartCount(
        monthKeyNursing,
        legacyMonthKeyNursing,
        chartCountFor("บันทึกทางการพยาบาล"),
        submissionDepartmentId,
      ),
      submissionDepartmentId,
    ),
  );
  const needleCharts = chartCountFor("เข็มตำ/ของมีคมบาด");
  const drugCharts = chartCountFor("นิเทศระบบยา");
  const sirenCharts = chartCountFor("SIREN bundle");
  const [supervisionCharts, setSupervisionCharts] = React.useState<number>(() =>
    getStoredChartCount(
      monthKeySupervision,
      legacyMonthKeySupervision,
      chartCountFor("นิเทศทางการพยาบาล"),
      submissionDepartmentId,
    ),
  );

  const [nursingSubmitted, setNursingSubmitted] = React.useState<boolean[]>(() =>
    Array(nursingCharts).fill(false),
  );
  const [nursingFilledCompleteness, setNursingFilledCompleteness] = React.useState<boolean[]>(() =>
    Array(nursingCharts).fill(false),
  );
  const [nursingAlert, setNursingAlert] = React.useState<boolean>(false);
  const [needleSubmitted, setNeedleSubmitted] = React.useState<boolean[]>(Array(needleCharts).fill(false));
  const [needleAlert, setNeedleAlert] = React.useState<boolean>(false);
  const [drugSubmitted, setDrugSubmitted] = React.useState<boolean[]>(Array(drugCharts).fill(false));
  const [drugFilledCompleteness, setDrugFilledCompleteness] = React.useState<boolean[]>(() =>
    Array(drugCharts).fill(false),
  );
  const [drugAlert, setDrugAlert] = React.useState<boolean>(false);
  const [sirenSubmitted, setSirenSubmitted] = React.useState<boolean[]>(Array(sirenCharts).fill(false));
  const [sirenAlert, setSirenAlert] = React.useState<boolean>(false);
  const [supervisionSubmitted, setSupervisionSubmitted] = React.useState<boolean[]>(() =>
    Array(supervisionCharts).fill(false),
  );
  const [supervisionAlert, setSupervisionAlert] = React.useState<boolean>(false);

  const recomputeFromStorage = React.useCallback(() => {
    try {
      const stored = readDraftWithFallback(monthKeyNursing, legacyMonthKeyNursing, submissionDepartmentId).data;
      if (!stored) {
        const fallbackCount = getStoredChartCount(
          monthKeyNursing,
          legacyMonthKeyNursing,
          chartCountFor("บันทึกทางการพยาบาล"),
          submissionDepartmentId,
        );
        setNursingCharts(fallbackCount);
        setChartTitleList(getDefaultNursingChartTitles(fallbackCount, submissionDepartmentId));
        setNursingSubmitted(Array(fallbackCount).fill(false));
        setNursingFilledCompleteness(Array(fallbackCount).fill(false));
        setNursingAlert(false);
        return;
      }
      const s = stored;
      const { answers: storedAnswers, focus: storedFocus, treatments: storedTreatments, chartTitles: storedChartTitles } =
        normalizeSubmissionShape(s as Record<string, unknown>);
      const storedCount = computeChartCountFromData(s, chartCountFor("บันทึกทางการพยาบาล"));
      if (storedCount !== nursingCharts) setNursingCharts(storedCount);
      const sub: boolean[] = normalizeArray(s.submitted, storedCount, false);
      setNursingSubmitted(sub);
      const filled: boolean[] = makeArray(storedCount, (i) =>
        hasNursingDraftRowCompleteForProgress((storedAnswers[i] || []) as string[]),
      );
      setNursingFilledCompleteness(filled);
      const incomplete: boolean[] = normalizeArray(s.incomplete, storedCount, false);
      const storedTitles = storedChartTitles.filter((v) => typeof v === "string");
      const normalizedTitles = normalizeNursingChartTitles(
        storedTitles as string[],
        storedCount,
        getNursingChartTitleFallbackPool(submissionDepartmentId),
      );
      setChartTitleList(normalizedTitles);
      const hasDraft = (i: number) => {
        const a = (storedAnswers[i] || []) as string[];
        const p = (s.problems?.[i] || []) as string[];
        return !!(
          (a && a.some((v) => hasNonDefaultChoice(v))) ||
          (s.disease?.[i] || "").length ||
          (storedFocus[i] || "").length ||
          (storedTreatments[i] || "").length ||
          (s.suggestions?.[i] || "").length ||
          (p && p.some((v) => v && v.length > 0))
        );
      };
      const requiredSlots = nursingRequiredChartSlots;
      const effectiveComplete = (idx: number) => sub[idx] || filled[idx];
      let doneGlobal = 0;
      for (let idx = 0; idx < storedCount; idx += 1) {
        if (effectiveComplete(idx)) doneGlobal += 1;
      }
      const meetsRequired = doneGlobal >= requiredSlots;
      const checkAllSlots = (predicate: (idx: number) => boolean) => {
        for (let idx = 0; idx < storedCount; idx += 1) {
          if (predicate(idx)) return true;
        }
        return false;
      };
      const anyUnsubmitted = checkAllSlots((idx) => !effectiveComplete(idx));
      const anyIncomplete = checkAllSlots((idx) => incomplete[idx]);
      const anyDraft = checkAllSlots((idx) => !sub[idx] && hasDraft(idx) && !filled[idx]);
      setNursingAlert(meetsRequired ? false : anyIncomplete || anyDraft || anyUnsubmitted);
    } catch {
      setNursingAlert(false);
    }
  }, [chartCountFor, legacyMonthKeyNursing, monthKeyNursing, nursingCharts, nursingRequiredChartSlots, submissionDepartmentId]);

  React.useEffect(() => {
    recomputeFromStorageRef.current = recomputeFromStorage;
  }, [recomputeFromStorage]);

  const recomputeNeedleFromStorage = React.useCallback(() => {
    try {
      const stored = readDraftWithFallback(monthKeyNeedle, legacyMonthKeyNeedle, submissionDepartmentId).data;
      if (!stored) {
        setNeedleSubmitted(Array(needleCharts).fill(false));
        setNeedleAlert(false);
        return;
      }
      const s = stored;
      const { answers: storedAnswers } = normalizeSubmissionShape(s as Record<string, unknown>);
      const sub: boolean[] = s.submitted || Array(needleCharts).fill(false);
      setNeedleSubmitted(sub);
      const incomplete: boolean[] = s.incomplete || Array(needleCharts).fill(false);
      const hasDraft = (i: number) => {
        const a = (storedAnswers[i] || []) as string[];
        return !!(a && a.some((v) => hasNonDefaultChoice(v)));
      };
      const anyUnsubmitted = sub.some((v) => !v);
      const anyIncomplete = incomplete.some(Boolean);
      const anyDraft = sub.some((v, i) => !v && hasDraft(i));
      setNeedleAlert(anyIncomplete || anyDraft || anyUnsubmitted);
    } catch {
      setNeedleAlert(false);
    }
  }, [legacyMonthKeyNeedle, monthKeyNeedle, needleCharts, submissionDepartmentId]);

  const recomputeDrugFromStorage = React.useCallback(() => {
    try {
      const stored = readDraftWithFallback(monthKeyDrug, legacyMonthKeyDrug, submissionDepartmentId).data;
      if (!stored) {
        setDrugSubmitted((prev) => (prev.length === drugCharts && prev.every((v) => !v) ? prev : Array(drugCharts).fill(false)));
        setDrugFilledCompleteness((prev) =>
          prev.length === drugCharts && prev.every((v) => !v) ? prev : Array(drugCharts).fill(false),
        );
        setDrugAlert(false);
        return;
      }
      const s = stored;
      const { answers: storedAnswers } = normalizeSubmissionShape(s as Record<string, unknown>);
      const sub: boolean[] = normalizeArray(s.submitted, drugCharts, false);
      setDrugSubmitted(sub);
      const filled: boolean[] = makeArray(drugCharts, (i) =>
        hasNursingDraftRowCompleteForProgress((storedAnswers[i] || []) as string[]),
      );
      setDrugFilledCompleteness(filled);
      const incomplete: boolean[] = normalizeArray(s.incomplete, drugCharts, false);
      const hasDraft = (i: number) => {
        const a = (storedAnswers[i] || []) as string[];
        const p = (s.problems?.[i] || []) as string[];
        return !!(
          (a && a.some((v) => hasNonDefaultChoice(v))) ||
          (p && p.some((v) => v && v.length > 0))
        );
      };
      const effectiveComplete = (idx: number) => sub[idx] || filled[idx];
      const anyUnsubmitted = Array.from({ length: drugCharts }, (_, i) => i).some((i) => !effectiveComplete(i));
      const anyIncomplete = incomplete.some(Boolean);
      const anyDraft = sub.some((v, i) => !v && hasDraft(i) && !filled[i]);
      setDrugAlert(anyIncomplete || anyDraft || anyUnsubmitted);
    } catch {
      setDrugAlert(false);
    }
  }, [drugCharts, legacyMonthKeyDrug, monthKeyDrug, submissionDepartmentId]);

  const recomputeSirenFromStorage = React.useCallback(() => {
    try {
      const stored = readDraftWithFallback(monthKeySiren, legacyMonthKeySiren, submissionDepartmentId).data;
      if (!stored) {
        setSirenSubmitted(Array(sirenCharts).fill(false));
        setSirenAlert(false);
        return;
      }
      const s = stored;
      const nested =
        s.data && typeof s.data === "object" && !Array.isArray(s.data)
          ? (s.data as Record<string, unknown>)
          : null;
      const { answers: storedAnswers, focus: storedFocus, treatments: storedTreatments } = normalizeSubmissionShape(
        s as Record<string, unknown>,
      );
      const rawAnswers = s.answers ?? nested?.answers;
      const sub = sirenColumnCompletionFlags({
        answers: Array.isArray(rawAnswers) && rawAnswers.length > 0 ? rawAnswers : storedAnswers,
        submitted: Array.isArray(s.submitted) ? s.submitted : nested?.submitted,
      });
      setSirenSubmitted(sub);
      const incomplete: boolean[] = s.incomplete || Array(sirenCharts).fill(false);
      const hasDraft = (i: number) => {
        const a = (storedAnswers[i] || []) as string[];
        const p = (s.problems?.[i] || []) as string[];
        const sh = (s.shifts?.[i] || []) as string[];
        const rawDate = s.dates?.[i];
        const dt = Array.isArray(rawDate) ? rawDate[0] || "" : (rawDate as string) || "";
        return !!(
          (a && a.some((v) => hasNonDefaultChoice(v))) ||
          (s.disease?.[i] || "").length ||
          (storedFocus[i] || "").length ||
          (storedTreatments[i] || "").length ||
          (s.suggestions?.[i] || "").length ||
          (p && p.some((v) => v && v.length > 0)) ||
          (sh && sh.some((v) => v && v.length > 0)) ||
          (dt && dt.length > 0) ||
          (s.hn?.[i] || "").length
        );
      };
      const anyUnsubmitted = sub.some((v) => !v);
      const anyIncomplete = incomplete.some(Boolean);
      const anyDraft = sub.some((v, i) => !v && hasDraft(i));
      setSirenAlert(anyIncomplete || anyDraft || anyUnsubmitted);
    } catch {
      setSirenAlert(false);
    }
  }, [legacyMonthKeySiren, monthKeySiren, sirenCharts, submissionDepartmentId]);

  const recomputeSupervisionFromStorage = React.useCallback(() => {
    try {
      const stored = readDraftWithFallback(monthKeySupervision, legacyMonthKeySupervision, submissionDepartmentId).data;
      if (!stored) {
        const fallback = getStoredChartCount(
          monthKeySupervision,
          legacyMonthKeySupervision,
          chartCountFor("นิเทศทางการพยาบาล"),
          submissionDepartmentId,
        );
        setSupervisionCharts(fallback);
        setSupervisionSubmitted(Array(fallback).fill(false));
        setSupervisionAlert(false);
        return;
      }
      const s = stored;
      const storedCount = computeChartCountFromData(s, chartCountFor("นิเทศทางการพยาบาล"));
      if (storedCount !== supervisionCharts) setSupervisionCharts(storedCount);
      const sub = normalizeArray(s.submitted, storedCount, false);
      setSupervisionSubmitted(sub);
      const incomplete = normalizeArray(s.incomplete, storedCount, false);
      const records = normalizeSupervisionRecords(s.supervisionRecords, storedCount);
      const storedDates = Array.isArray(s.dates)
        ? (s.dates as any[]).map((d) => (typeof d === "string" ? d : Array.isArray(d) ? d[0] ?? "" : ""))
        : [];
      const normalizedDates = normalizeArray(storedDates, storedCount, "");
      const hasDraft = (i: number) => {
        const record = records[i] || createSupervisionRecord();
        return !!(
          (normalizedDates[i] || "").length ||
          record.recipients.length ||
          record.recipientsDetail.trim().length ||
          record.activities.length ||
          record.issues.length ||
          record.issueOther.trim().length ||
          record.problemDetail.trim().length ||
          record.solution.trim().length ||
          record.followUp.trim().length
        );
      };
      const anyUnsubmitted = sub.some((v) => !v);
      const anyIncomplete = incomplete.some(Boolean);
      const anyDraft = sub.some((v, i) => !v && hasDraft(i));
      setSupervisionAlert(anyIncomplete || anyDraft || anyUnsubmitted);
    } catch {
      setSupervisionAlert(false);
    }
  }, [legacyMonthKeySupervision, monthKeySupervision, submissionDepartmentId, supervisionCharts]);

  React.useEffect(() => {
    if (hydrationState.loading) return;

    // Call latest recomputers via refs — do not list recompute* callbacks as deps.
    // Those callbacks close over chart state they set, so including them re-fires this
    // effect after every setState and infinite-loops (Maximum update depth exceeded).
    recomputeFromStorageRef.current();
    recomputeNeedleFromStorage();
    recomputeDrugFromStorage();
    recomputeSirenFromStorage();
    recomputeSupervisionFromStorage();
    // eslint-disable-next-line react-hooks/exhaustive-deps -- intentional stable trigger: hydration only
  }, [hydrationState.hydratedCategories, hydrationState.loading]);

  // Use stable keys (monthKey) instead of callback refs to avoid effect loops.
  // Callbacks change when state changes, which would re-trigger effects and cause flickering.
  React.useEffect(() => {
    recomputeNeedleFromStorage();
  }, [legacyMonthKeyNeedle, monthKeyNeedle]);

  React.useEffect(() => {
    recomputeFromStorage();
  }, [legacyMonthKeyNursing, monthKeyNursing]);

  React.useEffect(() => {
    recomputeDrugFromStorage();
  }, [legacyMonthKeyDrug, monthKeyDrug]);

  React.useEffect(() => {
    recomputeSirenFromStorage();
  }, [legacyMonthKeySiren, monthKeySiren]);

  React.useEffect(() => {
    recomputeSupervisionFromStorage();
  }, [legacyMonthKeySupervision, monthKeySupervision]);

  const handleSubmitChart = React.useCallback(
    (chartIndex: number, _incomplete?: boolean, revert?: boolean) => {
      const card = activeCard;
      const value = revert ? false : true;
      if (card) {
        const title = card.title;
        // Helper: ensure array is long enough, then set value (allows progress to update when panel has more charts than sidebar state)
        const updateSubmitted = (prev: boolean[]) => {
          const next = [...prev];
          while (next.length <= chartIndex) next.push(false);
          if (chartIndex >= 0) next[chartIndex] = value;
          return next;
        };
        if (title === LAUNCH_CATEGORY_TITLE) {
          setNursingSubmitted(updateSubmitted);
        } else if (title === NEEDLE_AND_SHARP_TITLE) {
          setNeedleSubmitted(updateSubmitted);
        } else if (title === MED_ERROR_TITLE) {
          setDrugSubmitted(updateSubmitted);
        } else if (title === SIREN_BUNDLE_TITLE) {
          setSirenSubmitted(updateSubmitted);
        } else if (title === "นิเทศทางการพยาบาล") {
          setSupervisionSubmitted(updateSubmitted);
        }
      }
      // Progress bar updates immediately from the setState calls above; do not run
      // recomputeFromStorage here — it reads localStorage which is only updated
      // after applySubmissionState() runs (after the async write), so it would
      // overwrite the optimistic progress with stale data.
      setReportingTableRefreshKey((key) => key + 1);
      // Keep UI feedback immediate: retrying hydration here can fetch stale Firestore data
      // and overwrite the optimistic card progress before the submit write completes.
    },
    [activeCard, setReportingTableRefreshKey],
  );

  const handleCloseCompliancePanel = React.useCallback(() => {
    setActiveCard(null);
    // recomputeFromStorage runs via onExitComplete when the SidePanel exit animation finishes,
    // ensuring the panel has unmounted and flushed its draft to localStorage first.
  }, []);

  const handleNursingFilledCompletenessChange = React.useCallback((filled: boolean[]) => {
    setNursingFilledCompleteness((prev) => {
      const next = [...prev];
      filled.forEach((v, i) => {
        next[i] = v;
      });
      if (filled.length > next.length) {
        for (let i = next.length; i < filled.length; i++) next.push(filled[i]);
      }
      return next;
    });
  }, []);

  const handleDrugFilledCompletenessChange = React.useCallback((filled: boolean[]) => {
    setDrugFilledCompleteness((prev) => {
      const next = [...prev];
      filled.forEach((v, i) => {
        next[i] = v;
      });
      if (filled.length > next.length) {
        for (let i = next.length; i < filled.length; i++) next.push(filled[i]);
      }
      return next;
    });
  }, []);

  const nursingProgress = computeNursingCardProgress(
    nursingCharts,
    nursingSubmitted,
    nursingFilledCompleteness,
    nursingRequiredChartSlots,
  );
  const needleProgress = computeProgressPercent(needleSubmitted.filter(Boolean).length, needleCharts);
  const drugProgress = computeNursingCardProgress(
    Math.max(drugSubmitted.length, drugFilledCompleteness.length, REQUIRED_MED_ERROR_CHARTS),
    drugSubmitted,
    drugFilledCompleteness,
    REQUIRED_MED_ERROR_CHARTS,
  );
  const sirenProgress = computeProgressPercent(sirenSubmitted.filter(Boolean).length, sirenCharts);
  const supervisionProgress = computeProgressPercent(
    supervisionSubmitted.filter(Boolean).length,
    supervisionCharts,
  );
  const complianceCards = React.useMemo(
    () =>
      DEFAULT_COMPLIANCE_CATEGORIES.map((category, index) => ({
        id: index + 1,
        title: category.title,
        categoryDocId: category.id ?? resolveCategoryDocId(category.title, complianceCategories),
        subtitle: category.helpText ?? formatPeriodicityLabel(category.periodicity),
        progress: 0,
        color: category.color ?? getPalette().primary,
      })),
    [complianceCategories],
  );

  const cards = React.useMemo(
    () => {
      const enriched = complianceCards.map((c) => {
        const iconColor = GRAY_ICON_TITLES.has(c.title) ? INACTIVE_ICON_COLOR : c.color;
        let progress = c.progress;
        let alertActive = false;

        if (c.title === LAUNCH_CATEGORY_TITLE) {
          progress = nursingProgress;
          alertActive = nursingAlert;
        } else if (c.title === NEEDLE_AND_SHARP_TITLE) {
          progress = needleProgress;
          alertActive = needleAlert;
        } else if (c.title === MED_ERROR_TITLE) {
          progress = drugProgress;
          alertActive = drugAlert;
        } else if (c.title === SIREN_BUNDLE_TITLE) {
          progress = sirenProgress;
          alertActive = sirenAlert;
        } else if (c.title === "นิเทศทางการพยาบาล") {
          progress = supervisionProgress;
          alertActive = supervisionAlert;
        }

        const showAlert =
          progress < 100 &&
          (c.title === LAUNCH_CATEGORY_TITLE ||
            c.title === MED_ERROR_TITLE ||
            c.title === SIREN_BUNDLE_TITLE
            ? true
            : CARD_WARNING_TITLES.has(c.title) && alertActive);
        const alertText = showAlert ? "มีแบบฟอร์มที่ยังไม่ส่งหรือส่งไม่ครบ" : undefined;

        return {
          ...c,
          progress,
          alertText,
          warning: showAlert,
          iconColor,
        };
      });

      const orderedTitles = [
        LAUNCH_CATEGORY_TITLE,
        "นิเทศทางการพยาบาล",
        NEEDLE_AND_SHARP_TITLE,
        MED_ERROR_TITLE,
        SIREN_BUNDLE_TITLE,
      ];
      const byTitle = (a: { title: string }, b: { title: string }) => a.title.localeCompare(b.title, "th");
      const byPosition = (a: { title: string }, b: { title: string }) => {
        const aIndex = orderedTitles.indexOf(a.title);
        const bIndex = orderedTitles.indexOf(b.title);
        const aRank = aIndex === -1 ? Number.POSITIVE_INFINITY : aIndex;
        const bRank = bIndex === -1 ? Number.POSITIVE_INFINITY : bIndex;
        if (aRank !== bRank) return aRank - bRank;
        return byTitle(a, b);
      };

      return [...enriched].sort(byPosition);
    },
    [
      complianceCards,
      drugAlert,
      drugProgress,
      needleAlert,
      needleProgress,
      nursingAlert,
      nursingProgress,
      sirenAlert,
      sirenProgress,
      supervisionAlert,
      supervisionProgress,
    ],
  );

  React.useEffect(() => {
    if (
      !shouldForceHeadWardNurseHomeTab({
        isHeadWardNurse,
        isSupervisorNurseOrAudit,
        cardCount: cards.length,
        openExportTabFromQuery,
        reportingCategoryFromQuery,
      })
    ) {
      return;
    }

    setActiveTab((current) => (current === "Home" || current === "ส่งออก" ? current : "Home"));
  }, [
    cards.length,
    isHeadWardNurse,
    isSupervisorNurseOrAudit,
    openExportTabFromQuery,
    reportingCategoryFromQuery,
    setActiveTab,
  ]);

  const handleReportingSubmissionEdit = React.useCallback(
    (submission: ReportingSubmission) => {
      const submissionCategoryId =
        submission.categoryId ??
        resolveCategoryDocId(submission.categoryTitle ?? submission.category, complianceCategories);
      const submissionCategoryTitle = submission.categoryTitle ?? submission.category;
      const retiredLookup = {
        categoryDocId: submissionCategoryId,
        title: submissionCategoryTitle,
      };

      // Prefer a visible closed notice even when the user cannot edit.
      if (isWardSubmitRetired(retiredLookup)) {
        showWardSubmitOpenClosedNotice(retiredLookup);
        return;
      }

      if (!canEditCompliance) return;

      const targetDepartmentId = submission.departmentId ?? activeDepartmentId ?? defaultDepartmentId;
      const hasDepartmentAccess =
        hasWildcardDepartmentScope ||
        (!!targetDepartmentId && scopedDepartments.some((dept) => dept.id === targetDepartmentId));

      if (!hasDepartmentAccess || !targetDepartmentId) {
        console.warn('Attempted to edit submission without department access', submission);
        return;
      }

      if (!submissionCategoryId && !submissionCategoryTitle) {
        console.warn('Attempted to edit submission without category access', submission);
        return;
      }

      const targetCard = cards.find(
        (card) => card.categoryDocId === submissionCategoryId || card.title === submissionCategoryTitle,
      );
      const targetCardTitle = targetCard?.title ?? submissionCategoryTitle ?? "";

      if (DEVELOPMENT_ONLY_TITLES.has(targetCardTitle)) {
        showDevelopmentModal(targetCardTitle, targetCard);
        return;
      }

      if (!allowedCategories.includes(targetCardTitle)) {
        console.warn('Attempted to edit submission without category access', submission);
        return;
      }

      const targetMonth = submission.reportingMonth ?? reportingMonth;

      void logAuditEvent({
        userId: currentUserId ?? null,
        resource: 'compliance',
        action: 'open',
        allowed: true,
        resourceId: targetDepartmentId,
        metadata: {
          source: 'reporting-table',
          reportingMonth: targetMonth,
          category: targetCardTitle,
          submissionId: submission.id,
        },
      });

      setActiveTab(landingTabLabel);
      setActiveDepartmentId(targetDepartmentId);
      setActiveReportingMonth(targetMonth);

      if (targetCard) {
        tryOpenChecklistCard(targetCard);
      } else {
        console.warn('No matching card found for category', targetCardTitle);
      }
    },
    [
      activeDepartmentId,
      allowedCategories,
      canEditCompliance,
      cards,
      complianceCategories,
      currentUserId,
      defaultDepartmentId,
      hasWildcardDepartmentScope,
      reportingMonth,
      scopedDepartments,
      setActiveReportingMonth,
      showDevelopmentModal,
      showWardSubmitOpenClosedNotice,
      tryOpenChecklistCard,
      landingTabLabel,
    ],
  );

  React.useEffect(() => {
    if (!activeCard) return;
    if (!isWardSubmitRetired({ categoryDocId: activeCard.categoryDocId, title: activeCard.title })) {
      return;
    }
    showWardSubmitOpenClosedNotice({
      categoryDocId: activeCard.categoryDocId,
      title: activeCard.title,
    });
  }, [activeCard, showWardSubmitOpenClosedNotice]);

  const dashboardPanel = canViewDashboard ? (
    <Suspense fallback={<div className="flex items-center justify-center rounded-2xl border border-slate-200 bg-white/60 p-12"><div className="h-8 w-8 animate-spin rounded-full border-2 border-indigo-500 border-t-transparent" /></div>}>
        <Dashboard
        roleScope={effectiveRoleScope}
        roleContext={roleContext}
        reportingScope={reportingScopeOverrides}
        mode="default"
        teamAllowedCategoryIds={roleContext?.teamAllowedCategoryIds}
        preselectedCategoryId={dashboardPreselectCategory}
        onPreselectConsumed={() => setDashboardPreselectCategory(null)}
        hideInternalFilters={activeTab === SUPERVISOR_REPORTING_TAB}
        teamDepartmentClientFilterIds={shellTeamDepartmentClientFilterIds}
        shellNurseNotesPeriodControls={shellNurseNotesPeriodControls}
      />
    </Suspense>
  ) : (
    <AccessGuard message="สิทธิ์การดูแดชบอร์ดถูกจำกัดสำหรับบทบาทของคุณ" />
  );

  const nurseNotesScoreboardLofDepartmentIds = React.useMemo(() => {
    if (!isSupervisorNurseRole || isNNTeamRole) return undefined;
    if (lofDepartmentIds.length > 0) return lofDepartmentIds;
    const fromAuth = (roleContext?.lineOfWorkDepartmentIds ?? []).filter(Boolean);
    return fromAuth.length > 0 ? fromAuth : [];
  }, [
    isNNTeamRole,
    isSupervisorNurseRole,
    lofDepartmentIds,
    roleContext?.lineOfWorkDepartmentIds,
  ]);

  const nurseNotesScoreboardScopeLabel = React.useMemo(() => {
    if (isNNTeamRole) return NURSE_NOTES_TEAM_LABEL;
    if (selectedLofRef?.id) return lofLabelForUi(selectedLofRef.id);
    return "หน่วยงานในสายงาน";
  }, [isNNTeamRole, selectedLofRef?.id]);

  const showNurseNotesScoreboard = isNNTeamRole || isSupervisorNurseRole;

  const nurseNotesScoreboardPanel = showNurseNotesScoreboard ? (
    <Suspense fallback={null}>
      <NurseNotesSubmissionScoreboard
        scopeDepartmentIds={nurseNotesScoreboardLofDepartmentIds}
        scopeLabel={nurseNotesScoreboardScopeLabel}
        scopeLoading={
          isSupervisorNurseRole &&
          !isNNTeamRole &&
          lofDeptLoading &&
          (nurseNotesScoreboardLofDepartmentIds?.length ?? 0) === 0
        }
      />
    </Suspense>
  ) : null;

  const reportingTablePanel = canViewSummary ? (
    <Suspense fallback={<div className="flex items-center justify-center rounded-2xl border border-slate-200 bg-white/60 p-12"><div className="h-8 w-8 animate-spin rounded-full border-2 border-indigo-500 border-t-transparent" /></div>}>
      <ReportingTable
        roleContext={roleContext}
        roleScope={effectiveRoleScope}
        canEdit={canEditCompliance}
        onEditSubmission={handleReportingSubmissionEdit}
        refreshKey={reportingTableRefreshKey}
        reportingScope={reportingScopeOverrides}
        mode="default"
        teamAllowedCategoryIds={roleContext?.teamAllowedCategoryIds}
        hideInternalFilters={activeTab === SUPERVISOR_REPORTING_TAB}
        teamDepartmentClientFilterIds={shellTeamDepartmentClientFilterIds}
        shellNurseNotesPeriodControls={shellNurseNotesPeriodControls}
      />
    </Suspense>
  ) : (
    <AccessGuard message="สิทธิ์การดูตารางสรุปถูกจำกัดสำหรับบทบาทของคุณ" />
  );

  const debugScopeDepartments = React.useMemo(() => {
    const ids = new Set<string>([
      ...(roleScope?.departmentIds ?? []),
      ...(roleScope?.view?.departmentIds ?? []),
      ...(roleScope?.edit?.departmentIds ?? []),
    ]);
    return Array.from(ids).filter(Boolean);
  }, [roleScope?.departmentIds, roleScope?.edit?.departmentIds, roleScope?.view?.departmentIds]);

  const debugScopeTeams = React.useMemo(() => {
    const ids = new Set<string>([
      ...(roleScope?.teamIds ?? []),
      ...(roleScope?.view?.teamIds ?? []),
      ...(roleScope?.edit?.teamIds ?? []),
    ]);
    return Array.from(ids).filter(Boolean);
  }, [roleScope?.edit?.teamIds, roleScope?.teamIds, roleScope?.view?.teamIds]);

  const debugUserLofId = React.useMemo(
    () => (typeof firestoreUser?.lofId === "string" ? firestoreUser.lofId : userLofRef?.id ?? null),
    [firestoreUser?.lofId, userLofRef?.id],
  );

  const debugPanel = import.meta.env.DEV ? (
    <div className="rounded-xl border border-dashed border-indigo-200 bg-indigo-50/40 px-4 py-3 text-xs text-indigo-900">
      <div className="font-semibold uppercase tracking-wide text-[10px] text-indigo-500">Debug Panel</div>
      <div className="mt-2 space-y-1">
        <div>
          <span className="font-semibold">roleContext.generalRole:</span>{" "}
          {roleContext?.generalRole ?? "—"}
        </div>
        <div>
          <span className="font-semibold">roleScope departmentIds:</span>{" "}
          {debugScopeDepartments.length ? debugScopeDepartments.join(", ") : "—"}
        </div>
        <div>
          <span className="font-semibold">roleScope teamIds:</span>{" "}
          {debugScopeTeams.length ? debugScopeTeams.join(", ") : "—"}
        </div>
        <div>
          <span className="font-semibold">user lofId:</span> {debugUserLofId ?? "—"}
        </div>
        <div>
          <span className="font-semibold">user lofRef:</span>{" "}
          {userLofRef?.path ?? userLofRef?.id ?? "—"}
        </div>
      </div>
    </div>
  ) : null;

  return (
    <div
      className="min-h-screen w-full px-4 py-4 sm:p-6"
      style={{ background: "radial-gradient(1200px 600px at 10% -10%, #ffffff 0%, #f7f8ff 40%, #eef0ff 100%)" }}
    >
      <div className="mx-auto max-w-[1440px] grid grid-rows-[auto_1fr] grid-cols-1 gap-4 sm:gap-6">
        {!hideComplianceHubTabStrip ? (
          <div className="col-start-1 col-end-2">
            <HeaderBar
              embedTabBarOnly={appShellOutlet?.appShellShowsMainNav === true}
              activeTab={activeTab}
              onSelectTab={(tab) => {
                const target = tabs.find((t) => t.label === tab);
                if (target?.disabled) return;
                if (tab === teamResultsTabLabel) {
                  if (isNNTeamRole) {
                    navigate("/team");
                    return;
                  }
                  if (hasEthicTeamRole) {
                    navigate("/team/ethic");
                    return;
                  }
                  return;
                }
                if (tab === TG_PROJECT_TAB) {
                  navigate("/team/occ");
                  return;
                }
                if (tab === KPIS_NURSING_TAB) {
                  navigate("/kpis");
                  return;
                }
                setActiveTab(tab);
              }}
              tabs={tabs}
              qualityWorkDropdown={isSupervisorNurseOrAudit || isHeadWardNurse || isAdmin}
              qualityWorkLinks={QUALITY_WORK_LINKS}
            />
          </div>
        ) : null}
        <div className="col-start-1 space-y-4" role="region" aria-label="Report content">
          {debugPanel && <div className="px-6">{debugPanel}</div>}
          {requiresManualDepartmentSelection &&
            !isSupervisorNurseRole &&
            !isGlobalViewRole &&
            activeTab !== SUPERVISOR_REPORTING_TAB && (
            <div className="px-6 space-y-2">
              <div>
                <MultiSelectDropdown
                  label="เลือกแผนก"
                  multiple={false}
                  value={selectedDepartmentId}
                  placeholder="เลือกแผนกที่ต้องการส่งรายงาน"
                  options={mainTabDepartmentPickerOptions.map((o) => ({ value: o.ref.id, label: o.label }))}
                  onChange={(next) => {
                    if (!next) {
                      setSelectedDepartmentRef(null);
                      return;
                    }
                    const ref = mainTabDepartmentPickerOptions.find((o) => o.ref.id === next)?.ref ?? null;
                    setSelectedDepartmentRef(ref);
                  }}
                  searchable
                  showClear
                  className="max-w-none"
                />
              </div>
              {!selectedDepartmentId && (
                <div className="flex items-center gap-3 rounded-xl border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-800">
                  <Circle className="h-4 w-4" />
                  <span>กรุณาเลือกแผนกก่อนส่งข้อมูล</span>
                </div>
              )}
            </div>
          )}
          {activeTab === "Home" && (
            <div className="space-y-4">
              {complianceCategoriesError && (
                <div className="px-6">
                  <div className="flex items-center gap-3 rounded-xl border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-800">
                    <Circle className="h-4 w-4" />
                    <span>
                      {complianceCategoriesError} (กำลังใช้หมวดหมู่เริ่มต้นแทน)
                    </span>
                  </div>
                </div>
              )}
              {isHeadWardNurse && (
                <div className="px-6 space-y-2">
                  {hydrationState.loading && (
                    <div className="rounded-xl border border-slate-200 bg-white/70 px-4 py-3 text-sm text-slate-700">
                      กำลังดึงข้อมูลที่บันทึกไว้สำหรับหน่วยของคุณ...
                    </div>
                  )}
                  {hydrationState.error && (
                    <div className="flex items-center justify-between gap-3 rounded-xl border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">
                      <span>{hydrationState.error}</span>
                      <button
                        className="rounded-lg border border-red-200 bg-white/70 px-3 py-1 text-xs font-semibold text-red-700 transition hover:bg-white disabled:opacity-50"
                        onClick={() => {
                          console.info('Retrying hydration fetch');
                          hydrationState.retry();
                        }}
                        disabled={hydrationState.loading}
                      >
                        ลองอีกครั้ง
                      </button>
                    </div>
                  )}
                </div>
              )}
              {canViewCompliance ? (
                <div className="space-y-8 px-4 py-4 sm:px-6 sm:py-6 lg:px-8">
                  <section className="space-y-3">
                    <h2 className="px-1 text-lg font-semibold text-slate-800">KPI</h2>
                    <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 sm:gap-4 lg:grid-cols-3 xl:grid-cols-4">
                      <HomeNavCard label="KPI" icon={BarChart3} to="/kpis" />
                    </div>
                  </section>

                  <section className="space-y-3">
                    <h2 className="px-1 text-lg font-semibold text-slate-800">แบบฟอร์ม</h2>
                    <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 sm:gap-4 lg:grid-cols-3 xl:grid-cols-4">
                      {WARD_GOOGLE_FORM_LINKS.map((link) => (
                        <QualityLinkCard key={link.url} label={link.label} url={link.url} />
                      ))}
                    </div>
                  </section>

                  {showQualityWorkHomeCard ? (
                    <section className="space-y-3">
                      <h2 className="px-1 text-lg font-semibold text-slate-800">งานคุณภาพ</h2>
                      <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 sm:gap-4 lg:grid-cols-3 xl:grid-cols-4">
                        {sortedQualityWorkLinks.map((link) => (
                          <QualityLinkCard
                            key={link.url}
                            label={link.label}
                            url={link.url}
                            tier={link.tier === 'todo-quality' ? 'todo-quality' : undefined}
                          />
                        ))}
                      </div>
                    </section>
                  ) : null}

                  {reportHubLinks.length > 0 ? (
                    <section className="space-y-3">
                      <h2 className="px-1 text-lg font-semibold text-slate-800">รายงาน</h2>
                      <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 sm:gap-4 lg:grid-cols-3 xl:grid-cols-4">
                        {reportHubLinks.map((link) =>
                          'href' in link && link.href ? (
                            <HomeNavCard
                              key={link.key}
                              label={link.label}
                              icon={Shield}
                              href={link.href}
                              trailing="external"
                            />
                          ) : (
                            <HomeNavCard
                              key={link.key}
                              label={link.label}
                              icon={Shield}
                              to={link.to}
                              trailing="chevron"
                            />
                          ),
                        )}
                      </div>
                    </section>
                  ) : null}

                  {teamHubLinks.length > 0 ? (
                    <section className="space-y-3">
                      <h2 className="px-1 text-lg font-semibold text-slate-800">คณะทำงาน</h2>
                      <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 sm:gap-4 lg:grid-cols-3 xl:grid-cols-4">
                        {teamHubLinks.map((link) =>
                          'href' in link && link.href ? (
                            <HomeNavCard
                              key={link.key}
                              label={link.label}
                              icon={Users}
                              href={link.href}
                              trailing="external"
                            />
                          ) : (
                            <HomeNavCard
                              key={link.key}
                              label={link.label}
                              icon={Users}
                              to={link.to}
                              trailing="chevron"
                            />
                          ),
                        )}
                      </div>
                    </section>
                  ) : null}

                  {exportHubLinks.length > 0 ? (
                    <section className="space-y-3">
                      <h2 className="px-1 text-lg font-semibold text-slate-800">ส่งออก</h2>
                      <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 sm:gap-4 lg:grid-cols-3 xl:grid-cols-4">
                        {exportHubLinks.map((link) => (
                          <HomeNavCard
                            key={link.key}
                            label={link.label}
                            icon={FileText}
                            to={link.to}
                            trailing="chevron"
                          />
                        ))}
                      </div>
                    </section>
                  ) : null}
                </div>
              ) : (
                <AccessGuard message="คุณไม่มีสิทธิ์เข้าถึงแบบฟอร์มตรวจติดตาม" />
              )}
            </div>
          )}
          {activeTab === "ประวัติ" && (
            canViewCompliance ? (
              <Suspense fallback={<div className="flex items-center justify-center rounded-2xl border border-slate-200 bg-white/60 p-12"><div className="h-8 w-8 animate-spin rounded-full border-2 border-indigo-500 border-t-transparent" /></div>}>
                <SubmissionHistoryView
                  departmentId={isSupervisorNurseRole ? null : submissionDepartmentId}
                  departmentIds={isSupervisorNurseRole ? lofDepartmentIds : undefined}
                  categories={allowedCategories}
                  submittedBy={undefined}
                />
              </Suspense>
            ) : (
              <AccessGuard message="คุณไม่มีสิทธิ์ดูประวัติการส่งย้อนหลัง" />
            )
          )}
          {canViewSummary && (
            <SupervisorReportingFiltersProvider
              defaultCategoryId="ethicSurveys"
              syncCategoryFromQuery={reportingCategoryFromQuery}
            >
              {/* Shell sync bridges: must not setState([]) / equivalent every render */}
              <ReportComplianceSummaryCategoryBridge />
              <ReportShellNurseNotesNavResetSync />
              <ReportShellEthicNavResetSync />
              <ReportShellNurseNotesDeptFilterSync />
              <ReportShellNurseNotesPeriodSync />
              <ReportShellEthicDeptFilterSync />
              <ReportShellTeamDeptFilterSync active={shellTeamDeptFilterActive} />
              {activeTab === SUPERVISOR_REPORTING_TAB && (
                <div className="space-y-4">
                  {isSupervisorNurseRole && selectedLofRef && lofDeptLoading && (
                    <div className="rounded-xl border border-slate-200 bg-white/70 px-4 py-3 text-sm text-slate-700">
                      {NIDATA_LOADING_LABEL}
                    </div>
                  )}
                  {requiresManualDepartmentSelection && !isSupervisorNurseRole && !isGlobalViewRole && (
                  <div className="px-6 space-y-2">
                    <div>
                      <MultiSelectDropdown
                        label="เลือกแผนก"
                        multiple={false}
                        value={selectedDepartmentId}
                        placeholder="เลือกแผนกที่ต้องการส่งรายงาน"
                        options={departmentSelectionOptions.map((o) => ({ value: o.ref.id, label: o.label }))}
                        onChange={(next) => {
                          if (!next) {
                            setSelectedDepartmentRef(null);
                            return;
                          }
                          const ref = departmentSelectionOptions.find((o) => o.ref.id === next)?.ref ?? null;
                          setSelectedDepartmentRef(ref);
                        }}
                        searchable
                        showClear
                        className="max-w-none"
                      />
                    </div>
                    {!selectedDepartmentId && (
                      <div className="flex items-center gap-3 rounded-xl border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-800">
                        <Circle className="h-4 w-4" />
                        <span>กรุณาเลือกแผนกก่อนส่งข้อมูล</span>
                      </div>
                    )}
                  </div>
                )}
                <Suspense fallback={null}>
                  <SupervisorReportingFilterBar
                    hideInlineEthicDepartmentFilter={
                      shellTeamDeptFilterActive || shellReportEthicDeptFilterActive
                    }
                    effectiveDepartmentIds={
                      isSupervisorNurseRole
                        ? (roleContext?.lineOfWorkDepartmentIds?.length
                            ? roleContext.lineOfWorkDepartmentIds
                            : [])
                        : selectedDepartmentId
                          ? [selectedDepartmentId]
                          : isGlobalViewRole
                            ? ["*"]
                            : effectiveDepartmentRefsForSelection.map((r) => r.id)
                    }
                  />
                </Suspense>
                {nurseNotesScoreboardPanel}
                {dashboardPanel}
                {reportingTablePanel}
              </div>
              )}
              {activeTab === "Dashboard" && (
                <div className="space-y-4">
                  {nurseNotesScoreboardPanel}
                  {dashboardPanel}
                </div>
              )}
              {activeTab === "ตารางข้อมูล" && reportingTablePanel}
              {activeTab === "ส่งออก" && (
                <Suspense fallback={<div className="flex items-center justify-center rounded-2xl border border-slate-200 bg-white/60 p-12"><div className="h-8 w-8 animate-spin rounded-full border-2 border-indigo-500 border-t-transparent" /></div>}>
                  <ExportHub
                    roleScope={effectiveRoleScope}
                    roleContext={roleContext}
                    reportingScope={reportingScopeOverrides}
                    reportingMonth={reportingMonth}
                    onReportingMonthChange={setActiveReportingMonth}
                  />
                </Suspense>
              )}
            </SupervisorReportingFiltersProvider>
          )}
        </div>
      </div>

      {wardSubmitClosedNotice ? (
        <div
          className="fixed bottom-4 left-1/2 z-[60] w-[min(92vw,28rem)] -translate-x-1/2 rounded-2xl border border-amber-200 bg-amber-50 px-4 py-3 shadow-lg"
          role="status"
          aria-live="polite"
        >
          <p className="text-sm font-semibold text-amber-950">{wardSubmitClosedNotice.primary}</p>
          <p className="mt-1 text-sm text-amber-900">{wardSubmitClosedNotice.nextStep}</p>
          <div className="mt-3 flex justify-end">
            <button
              type="button"
              className="rounded-full bg-amber-900 px-4 py-1.5 text-sm font-semibold text-white hover:bg-amber-800"
              onClick={() => setWardSubmitClosedNotice(null)}
            >
              ปิด
            </button>
          </div>
        </div>
      ) : null}

      <AnimatePresence>
        {activeCard &&
        !isWardSubmitRetired({ categoryDocId: activeCard.categoryDocId, title: activeCard.title }) ? (
          <>
            <motion.div
              className="fixed inset-0 bg-black/30 z-40"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              onClick={handleCloseCompliancePanel}
            />
            <Suspense fallback={null}>
            {activeCard.title === SIREN_BUNDLE_TITLE ? (
              <SirenBundleForm
                key={`${submissionDepartmentId ?? 'none'}-${reportingMonthDate.toISOString()}`}
                departmentId={submissionDepartmentId}
                reportingMonthDate={reportingMonthDate}
                currentUserId={currentUserId}
                submissionMetadata={submissionMetadata}
                readOnly={!canEditCompliance}
                onClose={handleCloseCompliancePanel}
                onSaved={recomputeSirenFromStorage}
              />
            ) : (
            <ComplianceHubSidePanel
              card={activeCard}
              onClose={handleCloseCompliancePanel}
              onSubmitChart={handleSubmitChart}
              onReportingTableRefresh={() => setReportingTableRefreshKey((k) => k + 1)}
              onExitComplete={
                activeCard?.title === LAUNCH_CATEGORY_TITLE
                  ? recomputeFromStorage
                  : activeCard?.title === MED_ERROR_TITLE
                    ? recomputeDrugFromStorage
                    : undefined
              }
              onNursingFilledCompletenessChange={
                activeCard?.title === LAUNCH_CATEGORY_TITLE
                  ? handleNursingFilledCompletenessChange
                  : activeCard?.title === MED_ERROR_TITLE
                    ? handleDrugFilledCompletenessChange
                    : undefined
              }
              isHeadWardNurse={isHeadWardNurse}
              readOnly={!canEditCompliance}
              allowedCategories={allowedCategories}
              hydrationCategories={hydrationCategories}
              complianceCategories={complianceCategories}
              reportingMonthDate={reportingMonthDate}
              departmentId={submissionDepartmentId}
              scopedDepartments={
                activeCard?.title === LAUNCH_CATEGORY_TITLE
                  ? scopedDepartments.filter((d) => !NURSE_NOTES_UI_EXCLUDED_DEPARTMENT_IDS.has(d.id))
                  : activeCard?.title === MED_ERROR_TITLE
                    ? scopedDepartments.filter((d) => !isMedErrorSupervisionDepartmentExcluded(d.id))
                    : activeCard?.title === SIREN_BUNDLE_TITLE
                      ? scopedDepartments.filter((d) => !isSirenBundleDepartmentExcluded(d.id))
                      : activeCard?.title === NEEDLE_AND_SHARP_TITLE
                        ? scopedDepartments.filter((d) => !isNeedleAndSharpDepartmentExcluded(d.id))
                        : scopedDepartments
              }
              makeMonthKey={computeMonthKey}
              chartCountFor={chartCountFor}
              periodLabelFor={periodLabelFor}
              hydrationToken={hydrationToken}
              hydrationLoading={hydrationState.loading}
              crossAuditMergeDiagnostics={hydrationState.crossAuditMergeDiagnostics}
              currentUserId={currentUserId}
              submissionMetadata={submissionMetadata}
              selectedDepartmentRef={selectedDepartmentRef}
              selectedLofRef={selectedLofRef}
              requireLofRef={isSupervisorNurseRole}
              suppressDepartmentValidation={isSupervisorNurseRole}
            />
            )}
            </Suspense>
          </>
        ) : null}
      </AnimatePresence>

      <AnimatePresence>
        {developmentModalOpen && (
          <>
            <motion.div
              className="fixed inset-0 bg-black/40 z-50"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              onClick={closeDevelopmentModal}
            />
            <motion.div
              className="fixed inset-0 z-50 flex items-center justify-center p-4"
              initial={{ opacity: 0, scale: 0.98 }}
              animate={{ opacity: 1, scale: 1 }}
              exit={{ opacity: 0, scale: 0.98 }}
              transition={{ type: "spring", stiffness: 180, damping: 20 }}
              role="dialog"
              aria-modal="true"
              aria-label="กำลังพัฒนา"
            >
              <div className="w-full max-w-md rounded-2xl bg-white p-6 shadow-2xl">
                <h2 className="text-lg font-semibold text-slate-800">กำลังพัฒนา</h2>
                <p className="mt-3 text-sm text-slate-600">
                  {developmentModalCategory ?? "หมวดหมู่"} กำลังอยู่ในช่วงพัฒนา ตอนนี้ลงใน Google Sheet ก่อนนะคะ
                </p>
                <div className="mt-6 flex justify-end">
                  <button
                    type="button"
                    className="rounded-full bg-slate-900 px-5 py-2 text-sm font-semibold text-white hover:bg-slate-800"
                    onClick={closeDevelopmentModal}
                  >
                    ok
                  </button>
                </div>
              </div>
            </motion.div>
          </>
        )}
      </AnimatePresence>
    </div>
  );
}
